/**
 * Home.js
 * 
 * Этот файл содержит компонент Home, который отвечает за отображение главной страницы приложения TPH Truck Parking.
 * Компонент управляет навигацией между списком локаций и карточками цен с использованием React Router.
 * 
 * Основные функции:
 * - Обработка URL-параметров для перенаправления на страницу цен конкретной локации.
 * - Отображение списка локаций или карточек цен в зависимости от маршрута.
 * - Управление состоянием для бронирования и отображения модальных окон.
 */
import React, { useState, useRef, useContext, useEffect, useCallback, useMemo, Suspense, lazy, useLayoutEffect } from 'react';
import axios from 'axios';
import { AppContext } from '../context/AppContext';
import { prewarmHotToast } from 'react-hot-toast';
import keywords from '../config/SEOkeywords';
import '../styles/global.css';
import { calculateSavings } from '../utils/bookingCalculations';
import {
  getLocationRates,
  getLocationPromoDiscount,
  calculatePromoPrice,
  getPromoDiscountLabel,
  getLocationMonthlyDisplayMeta,
  getLocationNoContractRate,
} from '../utils/pricing';
import useMapState from '../hooks/useMapState';
import { buildLocationPricingSchemas, generateSEOProps } from '../utils/seoUtils';
import { generateMapComponentProps } from '../utils/mapUtils';
import { getSectionByPath } from '../config/sectionNav';
import { getLocationStateBySlug, getLocationStatePathByCode } from '../config/locationStates';
import { CANONICAL_BASE_URL, getRouteSeoMeta } from '../config/routeSeo';
import {
  buildCanonicalPricingPath,
  isCanonicalPricingPath,
  parseAddressParts,
  parsePricingPath,
  sanitizeLocationId,
} from '../utils/pricingPath';
import {
  buildTruckParkingDiscoveryDataset,
  TRUCK_PARKING_BASE_PATH,
  buildTruckParkingCityPath,
  filterLocationsForTruckParkingRoute,
  getLocationTruckParkingRouteMeta,
  parseTruckParkingPath,
} from '../utils/truckParkingDiscovery';
import {
  buildTruckParkingHomepageContent,
  buildTruckParkingRouteContent,
} from '../utils/truckParkingDiscoveryContent';
import { getResponsiveRightEdgeShiftConfig } from '../utils/mapFrameConstants';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BuildingStorefrontIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { fetchAds, logAdEvent } from '../services/adsService';
import { logEvent } from '../services/analyticsService';
import { fetchGoogleRatingSummary } from '../services/googleReviews';
import { todayEST } from '../utils/dateLite';
import AdSlider from '../components/AdSlider.tsx';
import { featureFlags } from '../config/featureFlags';
const HomepageHiringRail = lazy(() => import('../components/HomepageHiringRail'));
const LocationSeoSections = lazy(() => import('../components/location/LocationSeoSections.tsx'));
const TruckParkingDiscoverySections = lazy(() => import('../components/location/TruckParkingDiscoverySections'));
const ListYourLotContent = lazy(() => import('../components/landing/ListYourLotContent'));

// Add validateBookingRequest pure function
export const validateBookingRequest = (locationId, location, availableLots, lotNumberAndId) => {
  if (!locationId) {
    return { valid: false, message: 'Location not found. Please try again.' };
  }
  
  if (!location) {
    return { valid: false, message: 'Location details not found. Please try again.' };
  }

  if (location.isBookingBlocked) {
    return { valid: false, message: 'This location is fully booked. Please select a different location.' };
  }

  // Always allow booking - booking modal will handle spot assignment and date selection
  return { valid: true };
};

// Add parseLocationFromUrl pure function
export const parseLocationFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('locationId');
  } catch (error) {
    console.error('Error parsing URL parameters:', error);
    return null;
  }
};

const decodeHeadingSlug = (value = '') => (
  String(value || '')
    .split('-')
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ')
);

const stripVisibleBrandSuffix = (value = '') => (
  String(value || '')
    .replace(/\s*\|\s*Right Solution Truck Parking$/i, '')
    .replace(/\s*\|\s*Right Solution$/i, '')
    .trim()
);

const buildTruckParkingIntentHeading = (intentSlug = '') => {
  const normalizedIntentSlug = String(intentSlug || '').trim().toLowerCase();
  if (!normalizedIntentSlug) return 'Parking Locations';
  if (normalizedIntentSlug === 'overnight') return 'Overnight Parking';
  if (normalizedIntentSlug === 'secure') return 'Secure Parking';
  if (normalizedIntentSlug === 'monthly') return 'Monthly Parking';
  if (normalizedIntentSlug.startsWith('near-')) {
    const corridorLabel = decodeHeadingSlug(normalizedIntentSlug.replace(/^near-/, ''));
    return corridorLabel ? `Parking Near ${corridorLabel}` : 'Parking Locations';
  }

  return 'Parking Locations';
};

const buildCompactLocationsHeading = ({ routeMeta = null, fallbackTitle = '' } = {}) => {
  if (!routeMeta?.isTruckParkingRoute) {
    return stripVisibleBrandSuffix(fallbackTitle) || 'Locations';
  }

  const stateName = getLocationStateBySlug(routeMeta.stateSlug || '')?.name || decodeHeadingSlug(routeMeta.stateSlug);
  const cityName = decodeHeadingSlug(routeMeta.citySlug);

  if (routeMeta.routeType === 'hub') {
    return 'East Coast Parking Location by State and City';
  }

  if (routeMeta.routeType === 'state') {
    return stateName ? `Parking Locations in ${stateName}` : 'Parking Locations';
  }

  if (routeMeta.routeType === 'city') {
    if (cityName && stateName) {
      return `Parking Locations in ${cityName}, ${stateName}`;
    }
    return cityName ? `Parking Locations in ${cityName}` : 'Parking Locations';
  }

  if (routeMeta.routeType === 'intent') {
    const intentHeading = buildTruckParkingIntentHeading(routeMeta.intentSlug);
    if (cityName && stateName) {
      return `${intentHeading} in ${cityName}, ${stateName}`;
    }
    return intentHeading;
  }

  return 'Locations';
};

const Card = lazy(() => import('../components/Card'));
const MapComponent = lazy(() => import('../components/TPHMap/MapComponent'));
const SearchBar = lazy(() => import('../components/SearchBar'));
const NoResultsCard = lazy(() => import('../components/NoResultsCard'));
const preloadBookingModal = () => import('../components/BookingFlow/BookingModal.tsx');
const BookingModal = lazy(preloadBookingModal);
const PricingCard = lazy(() => import('../components/PricingCard'));
const BookingConfirmation = lazy(() => import('../components/BookingFlow/BookingConfirmation'));
const StateCard = lazy(() => import('../components/StateCard'));
const StateCardSkeleton = lazy(() => import('../components/StateCardSkeleton'));
const StateCardList = lazy(() => import('../components/shared/StateCardList'));
const MessageModal = lazy(() => import('../components/MessageModal'));
const SEO = lazy(() => import('../components/SEO'));
const GoogleRatingDisplay = lazy(() => import('../components/GoogleRatingDisplay'));
const ReadReviewModal = lazy(() => import('../components/ReadReviewModal'));
const CardSkeleton = lazy(() => import('../components/CardSkeleton'));
const InlineReviews = lazy(() => import('../components/InlineReviews'));

const STATIC_MAP_MAX_MARKERS = 24;
const LOCAL_HOSTNAME_PATTERN = /^(localhost|127\.0\.0\.1|0\.0\.0\.0)$/i;
const STATIC_MAP_MARKER_SCALE = 1;
const STATIC_MAP_MARKER_ICON_FILENAME = 'tph-map-pin-edge-clear.png';
const STATIC_MAP_MARKER_ICON_VERSION =
  process.env.REACT_APP_STATIC_MAP_MARKER_ICON_VERSION || '20260715bulldog';
const STATIC_MAP_SIZE = Object.freeze({ width: 640, height: 225 });
const STATIC_MAP_SCALE = 2;
const STATIC_MAP_PADDING_PX = 26;
const DESKTOP_DEFAULT_OPEN_MAP_MIN_WIDTH = 1024;
const PRICING_PANEL_SWIPE_THRESHOLD_PX = 56;
const formatAmenityLabel = (value = '') => (
  String(value || '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .trim()
    .replace(/^./, (char) => char.toUpperCase())
);

const buildPricingAmenityGroups = (amenities = {}) => {
  const nearbyGasStations = Array.isArray(amenities.nearbyGasStations) ? amenities.nearbyGasStations : [];
  const nearbyHotels = Array.isArray(amenities.nearbyHotels) ? amenities.nearbyHotels : [];
  const nearbyFood = Array.isArray(amenities.nearbyFood) ? amenities.nearbyFood : [];
  const services = Array.isArray(amenities.services) ? amenities.services : [];

  const allFlagEntries = Object.entries(amenities).filter(([key, value]) => (
    !['nearbyGasStations', 'nearbyHotels', 'nearbyFood', 'services'].includes(key)
    && typeof value === 'boolean'
    && value
  ));

  const accessKeys = ['fenced', 'access24h', 'gateAccess', 'gateOpen', 'gateSlideIn', 'securityCameras', 'lights'];
  const surfaceKeys = ['paved', 'gravel', 'truckStopNearby', 'bathroom', 'restroomShowers', 'onSiteOffice'];
  const nearbyKeys = ['nearGas', 'fuelNearby', 'nearHotels', 'nearFood', 'convenience24h'];
  const supportKeys = ['serviceAvailable'];

  const takeLabels = (keys) => keys
    .filter((key) => amenities[key])
    .map((key) => formatAmenityLabel(key));
  const takeMissingLabels = (keys) => keys
    .filter((key) => amenities[key] === false)
    .map((key) => formatAmenityLabel(key));

  const unknownLabels = allFlagEntries
    .filter(([key]) => ![...accessKeys, ...surfaceKeys, ...nearbyKeys, ...supportKeys].includes(key))
    .map(([key]) => formatAmenityLabel(key));

  return [
    {
      key: 'access-security',
      title: 'Access & Security',
      eyebrow: 'Security',
      icon: ShieldCheckIcon,
      iconAccent: 'text-[#79b1ff]',
      items: [...takeLabels(accessKeys), ...unknownLabels],
      missingItems: takeMissingLabels(accessKeys),
      stat: `${takeLabels(accessKeys).length + unknownLabels.length} features`,
    },
    {
      key: 'surface-site',
      title: 'Surface & Site',
      eyebrow: 'Lot setup',
      icon: TruckIcon,
      iconAccent: 'text-[#63d187]',
      items: takeLabels(surfaceKeys),
      missingItems: takeMissingLabels(surfaceKeys),
      stat: `${takeLabels(surfaceKeys).length} features`,
    },
    {
      key: 'nearby-essentials',
      title: 'Nearby',
      eyebrow: 'Nearby',
      icon: BuildingStorefrontIcon,
      iconAccent: 'text-[#f3bb36]',
      items: [
        ...takeLabels(nearbyKeys),
        ...(nearbyGasStations.length ? [`${nearbyGasStations.length} gas stop${nearbyGasStations.length === 1 ? '' : 's'}`] : []),
        ...(nearbyHotels.length ? [`${nearbyHotels.length} hotel${nearbyHotels.length === 1 ? '' : 's'}`] : []),
        ...(nearbyFood.length ? [`${nearbyFood.length} food option${nearbyFood.length === 1 ? '' : 's'}`] : []),
      ],
      missingItems: [
        ...takeMissingLabels(nearbyKeys),
        ...(nearbyHotels.length === 0 ? ['Hotels'] : []),
        ...(nearbyFood.length === 0 ? ['Food'] : []),
      ],
      stat: `${nearbyGasStations.length + nearbyHotels.length + nearbyFood.length} nearby`,
    },
    {
      key: 'support-services',
      title: 'On-Site Facilities',
      eyebrow: 'On-site',
      icon: WrenchScrewdriverIcon,
      iconAccent: 'text-[#f07d99]',
      items: [
        ...takeLabels(supportKeys),
        ...services.map((service) => service?.name).filter(Boolean),
      ],
      missingItems: takeMissingLabels(['serviceAvailable', 'restroomShowers', 'bathroom', 'onSiteOffice']),
      stat: `${services.length} service${services.length === 1 ? '' : 's'}`,
    },
  ].map((group) => ({
    ...group,
    items: Array.from(new Set(group.items.filter(Boolean))),
    missingItems: Array.from(new Set((group.missingItems || []).filter(Boolean))),
  })).filter((group) => group.items.length > 0);
};

const chunkAmenityGroups = (groups = [], size = 2) => {
  const normalizedSize = Math.max(1, size);
  const pages = [];
  for (let index = 0; index < groups.length; index += normalizedSize) {
    pages.push(groups.slice(index, index + normalizedSize));
  }
  return pages;
};

const getPricingCardSavings = ({
  duration,
  isPromoActive = false,
  baseDailyRate,
  baseWeeklyRate,
  displayedDailyRate,
  displayedWeeklyRate,
  monthlyRate,
  noContractRate,
}) => {
  if (isPromoActive && duration === 'per day') {
    return Math.max(0, Math.round(Number(baseDailyRate) - Number(displayedDailyRate)));
  }

  if (isPromoActive && duration === 'per week') {
    return Math.max(0, Math.round(Number(baseWeeklyRate) - Number(displayedWeeklyRate)));
  }

  return calculateSavings(
    duration,
    baseDailyRate,
    baseWeeklyRate,
    monthlyRate,
    noContractRate
  );
};

const getStaticMapViewportWidth = () => {
  if (typeof window === 'undefined') return STATIC_MAP_SIZE.width;
  const viewportWidth = window.innerWidth || STATIC_MAP_SIZE.width;
  const horizontalPadding = viewportWidth >= 768 ? 32 : viewportWidth >= 640 ? 24 : 16;
  return Math.max(320, Math.min(1280, viewportWidth - horizontalPadding));
};

const getValidCoordinates = (location) => {
  const lat = Number(location?.coordinates?.lat);
  const lng = Number(location?.coordinates?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

const getAverageCoordinates = (coordinates) => {
  if (!Array.isArray(coordinates) || coordinates.length === 0) return null;
  const total = coordinates.reduce(
    (acc, point) => ({
      lat: acc.lat + point.lat,
      lng: acc.lng + point.lng,
    }),
    { lat: 0, lng: 0 }
  );
  return {
    lat: total.lat / coordinates.length,
    lng: total.lng / coordinates.length,
  };
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const areSameCoordinates = (a, b) => {
  if (!a || !b) return false;
  return Math.abs(a.lat - b.lat) < 1e-6 && Math.abs(a.lng - b.lng) < 1e-6;
};

const dedupeCoordinates = (coordinates) => {
  if (!Array.isArray(coordinates)) return [];
  const byKey = new Map();
  coordinates.forEach((coordinate) => {
    if (!coordinate) return;
    const key = `${Number(coordinate.lat).toFixed(6)},${Number(coordinate.lng).toFixed(6)}`;
    if (!byKey.has(key)) {
      byKey.set(key, coordinate);
    }
  });
  return [...byKey.values()];
};

const getCoordinateBounds = (coordinates) => {
  if (!Array.isArray(coordinates) || coordinates.length === 0) return null;

  return coordinates.reduce(
    (acc, point) => ({
      minLat: Math.min(acc.minLat, point.lat),
      maxLat: Math.max(acc.maxLat, point.lat),
      minLng: Math.min(acc.minLng, point.lng),
      maxLng: Math.max(acc.maxLng, point.lng),
    }),
    {
      minLat: Number.POSITIVE_INFINITY,
      maxLat: Number.NEGATIVE_INFINITY,
      minLng: Number.POSITIVE_INFINITY,
      maxLng: Number.NEGATIVE_INFINITY,
    }
  );
};

const getBoundsCenter = (bounds) => {
  if (!bounds) return null;
  return {
    lat: (bounds.minLat + bounds.maxLat) / 2,
    lng: (bounds.minLng + bounds.maxLng) / 2,
  };
};

const normalizeLongitude = (lng) => {
  if (!Number.isFinite(lng)) return lng;
  let next = lng;
  while (next > 180) next -= 360;
  while (next < -180) next += 360;
  return next;
};

const getMapDistanceScore = (a, b) => {
  if (!a || !b) return 0;
  const latDelta = a.lat - b.lat;
  const avgLat = ((a.lat + b.lat) / 2) * (Math.PI / 180);
  const lngDelta = (a.lng - b.lng) * Math.cos(avgLat);
  return (latDelta * latDelta) + (lngDelta * lngDelta);
};

const pickStaticPreviewMarkerCoordinates = (coordinates, maxMarkers, selectedCoordinate = null) => {
  const uniqueCoordinates = dedupeCoordinates(coordinates);
  if (uniqueCoordinates.length <= maxMarkers) return uniqueCoordinates;
  if (maxMarkers <= 0) return [];

  const selected = [];
  const addCoordinate = (candidate) => {
    if (!candidate) return;
    if (selected.some((entry) => areSameCoordinates(entry, candidate))) return;
    selected.push(candidate);
  };

  if (selectedCoordinate) {
    const matchingCoordinate = uniqueCoordinates.find((coordinate) => areSameCoordinates(coordinate, selectedCoordinate));
    addCoordinate(matchingCoordinate || selectedCoordinate);
  }

  const bounds = getCoordinateBounds(uniqueCoordinates);
  if (bounds) {
    addCoordinate(uniqueCoordinates.reduce((best, coordinate) => (coordinate.lat > best.lat ? coordinate : best), uniqueCoordinates[0]));
    addCoordinate(uniqueCoordinates.reduce((best, coordinate) => (coordinate.lat < best.lat ? coordinate : best), uniqueCoordinates[0]));
    addCoordinate(uniqueCoordinates.reduce((best, coordinate) => (coordinate.lng > best.lng ? coordinate : best), uniqueCoordinates[0]));
    addCoordinate(uniqueCoordinates.reduce((best, coordinate) => (coordinate.lng < best.lng ? coordinate : best), uniqueCoordinates[0]));
  }

  while (selected.length < maxMarkers) {
    let bestCandidate = null;
    let bestSpreadScore = Number.NEGATIVE_INFINITY;

    uniqueCoordinates.forEach((candidate) => {
      if (selected.some((entry) => areSameCoordinates(entry, candidate))) return;
      if (selected.length === 0) {
        bestCandidate = candidate;
        bestSpreadScore = Number.POSITIVE_INFINITY;
        return;
      }

      const closestDistance = selected.reduce((closest, chosen) => (
        Math.min(closest, getMapDistanceScore(candidate, chosen))
      ), Number.POSITIVE_INFINITY);

      if (closestDistance > bestSpreadScore) {
        bestSpreadScore = closestDistance;
        bestCandidate = candidate;
      }
    });

    if (!bestCandidate) break;
    selected.push(bestCandidate);
  }

  return selected.slice(0, maxMarkers);
};

const toMercatorY = (latitude) => {
  const boundedLat = clamp(latitude, -85.05112878, 85.05112878);
  const latRadians = boundedLat * (Math.PI / 180);
  const sinValue = Math.sin(latRadians);
  return 0.5 - (Math.log((1 + sinValue) / (1 - sinValue)) / (4 * Math.PI));
};

const computeStaticPreviewZoom = (
  bounds,
  {
    widthPx,
    heightPx,
    paddingPx = 0,
    minZoom = 3,
    maxZoom = 16,
  }
) => {
  if (!bounds) return minZoom;

  const usableWidth = Math.max(1, widthPx - (paddingPx * 2));
  const usableHeight = Math.max(1, heightPx - (paddingPx * 2));

  const latFraction = Math.max(1e-6, Math.abs(toMercatorY(bounds.maxLat) - toMercatorY(bounds.minLat)));
  const rawLngSpan = Math.abs(bounds.maxLng - bounds.minLng);
  const lngSpan = rawLngSpan > 180 ? 360 - rawLngSpan : rawLngSpan;
  const lngFraction = Math.max(1e-6, lngSpan / 360);

  const worldTileSize = 256;
  const latZoom = Math.log2(usableHeight / (worldTileSize * latFraction));
  const lngZoom = Math.log2(usableWidth / (worldTileSize * lngFraction));
  const zoom = Math.floor(Math.min(latZoom, lngZoom));

  return clamp(zoom, minZoom, maxZoom);
};

const trimTrailingSlash = (value = '') => String(value).replace(/\/+$/, '');

const resolveStaticMapMarkerIconUrl = () => {
  const explicitMarkerIconUrl = process.env.REACT_APP_STATIC_MAP_MARKER_ICON_URL;
  if (explicitMarkerIconUrl) return explicitMarkerIconUrl;

  if (typeof window !== 'undefined' && window.location?.origin) {
    const hostname = window.location.hostname || '';
    if (!LOCAL_HOSTNAME_PATTERN.test(hostname)) {
      return `${trimTrailingSlash(window.location.origin)}/${STATIC_MAP_MARKER_ICON_FILENAME}`;
    }
  }

  return `${trimTrailingSlash(CANONICAL_BASE_URL)}/${STATIC_MAP_MARKER_ICON_FILENAME}`;
};

const appendStaticMapMarkerVersion = (iconUrl) => {
  if (!iconUrl || !STATIC_MAP_MARKER_ICON_VERSION) return iconUrl;
  const separator = iconUrl.includes('?') ? '&' : '?';
  return `${iconUrl}${separator}v=${encodeURIComponent(STATIC_MAP_MARKER_ICON_VERSION)}`;
};

const LOCATION_INTENT_LAST_HOUR_PARKING = 'last_hour_parking';
const LOCATION_AVAILABILITY_NOW = 'now';
const LOCATION_SORT_LEGACY_URGENT_FIT = 'urgent_fit';
const LOCATION_SORT_PRICE_ASC = 'price_asc';
const LOCATION_SORT_AVAILABILITY_DESC = 'availability_desc';
const LOCATION_SORT_DISTANCE_ASC = 'distance_asc';
const LOCATION_SORT_OPTIONS = Object.freeze([
  LOCATION_SORT_PRICE_ASC,
  LOCATION_SORT_AVAILABILITY_DESC,
  LOCATION_SORT_DISTANCE_ASC,
]);
const LOCATION_SORT_QUICK_PICKS = Object.freeze([
  { key: LOCATION_SORT_AVAILABILITY_DESC, label: 'Most spots' },
  { key: LOCATION_SORT_DISTANCE_ASC, label: 'Nearest' },
]);
const LOCATION_SORT_FILTER_QUICK_PICKS = Object.freeze([
  { key: 'monthlyOnly', label: 'Monthly only' },
  { key: 'comingSoon', label: 'Coming soon' },
]);
const LOCATION_REFINEMENT_FILTERS = Object.freeze([
  { key: 'securityCameras', label: 'Security Cameras' },
  { key: 'pavedSurface', label: 'Paved Surface' },
  { key: 'gravelSurface', label: 'Gravel Surface' },
  { key: 'gated', label: 'Gated' },
  { key: 'openGate', label: 'Open Gate' },
  { key: 'bathrooms', label: 'Bathrooms' },
  { key: 'gasStationNearby', label: 'Gas Station Nearby' },
  { key: 'hotelsNearby', label: 'Hotels Nearby' },
  { key: 'monthlyAvailable', label: 'Monthly available' },
]);

export const parseLocationsRouteIntent = (search = '') => {
  try {
    const params = new URLSearchParams(search || '');
    const rawIntent = (params.get('intent') || '').trim().toLowerCase();
    const rawAvailability = (params.get('availability') || '').trim().toLowerCase();
    const rawSort = (params.get('sort') || '').trim().toLowerCase();
    const rawOpenForDaily = (params.get('openForDaily') || '').trim().toLowerCase();
    const rawOpenForParking = (params.get('openForParking') || '').trim().toLowerCase();

    const searchIntent = rawIntent === LOCATION_INTENT_LAST_HOUR_PARKING
      ? LOCATION_INTENT_LAST_HOUR_PARKING
      : null;
    const initialAvailabilityMode = rawAvailability === LOCATION_AVAILABILITY_NOW
      ? LOCATION_AVAILABILITY_NOW
      : null;
    const initialSortMode = rawSort === LOCATION_SORT_LEGACY_URGENT_FIT
      ? LOCATION_SORT_AVAILABILITY_DESC
      : (LOCATION_SORT_OPTIONS.includes(rawSort) ? rawSort : null);
    const requireOpenForDaily = rawOpenForDaily === 'true';
    const requireOpenForParking = rawOpenForParking === 'true';

    return {
      searchIntent,
      initialAvailabilityMode,
      initialSortMode,
      requireOpenForDaily,
      requireOpenForParking,
    };
  } catch (_error) {
    return {
      searchIntent: null,
      initialAvailabilityMode: null,
      initialSortMode: null,
      requireOpenForDaily: false,
      requireOpenForParking: false,
    };
  }
};

export const getDefaultQuickFilters = () => ({
  securityCameras: false,
  pavedSurface: false,
  gravelSurface: false,
  gated: false,
  openGate: false,
  bathrooms: false,
  gasStationNearby: false,
  hotelsNearby: false,
  monthlyAvailable: false,
  monthlyOnly: false,
  comingSoon: false,
});

export const getDefaultSortMode = (searchIntent, initialSortMode = null) => {
  if (searchIntent === LOCATION_INTENT_LAST_HOUR_PARKING) {
    return LOCATION_SORT_AVAILABILITY_DESC;
  }
  return initialSortMode || null;
};

export const getRouteScopedDiscoveryUiDefaults = ({
  isLocationsRoute = false,
  searchIntent = null,
  initialSortMode = null,
  initialAvailabilityMode = null,
} = {}) => ({
  quickFilters: getDefaultQuickFilters(),
  availabilityMode: isLocationsRoute ? (initialAvailabilityMode || null) : null,
  sortMode: isLocationsRoute ? getDefaultSortMode(searchIntent, initialSortMode) : null,
});

const getAvailableLotCount = (locationId, lotsAvailableByLocationId = {}) => (
  (locationId && lotsAvailableByLocationId?.[locationId]?.length) || 0
);

const getBookedLotCount = (locationId, lotsBookedByLocationId = {}) => (
  (locationId && lotsBookedByLocationId?.[locationId]?.length) || 0
);

const getLocationFlags = (location) => location?.flags || {};

export const isLocationEligibleForResults = (
  location,
  lotsAvailableByLocationId = {},
  lotsBookedByLocationId = {},
  options = {},
) => {
  const {
    includeMonthlyWithoutLotInventory = false,
  } = options || {};

  if (!location) return false;
  if (includeMonthlyWithoutLotInventory && location.openForMonthly === true) return true;
  if (location.availability === false) return false;
  if (location.openForParking === false) return true;

  const availableLots = getAvailableLotCount(location.id, lotsAvailableByLocationId);
  const bookedLots = getBookedLotCount(location.id, lotsBookedByLocationId);
  return (availableLots + bookedLots) > 0;
};

export const applyQuickFiltersToLocations = (
  locations = [],
  quickFilters = {},
) => (
  (Array.isArray(locations) ? locations : []).filter((location) => {
    const flags = getLocationFlags(location);
    const hasNearbyGas = !!(
      flags.nearGas === true
      || (Array.isArray(location?.nearbyGasStations) && location.nearbyGasStations.length > 0)
    );
    const hasNearbyHotels = !!(
      flags.nearHotels === true
      || (Array.isArray(location?.nearbyHotels) && location.nearbyHotels.length > 0)
    );
    if (quickFilters.gated && !(flags.gateAccess === true || flags.fenced === true)) {
      return false;
    }
    if (quickFilters.openGate && !(flags.gateOpen === true || flags.gateAccess === true || flags.gateSlideIn === true)) {
      return false;
    }
    if (quickFilters.pavedSurface && flags.paved !== true) {
      return false;
    }
    if (quickFilters.gravelSurface && flags.gravel !== true) {
      return false;
    }
    if (quickFilters.securityCameras && flags.securityCameras !== true) {
      return false;
    }
    if (quickFilters.bathrooms && !(flags.bathroom === true || flags.restroomShowers === true)) {
      return false;
    }
    if (quickFilters.gasStationNearby && !hasNearbyGas) {
      return false;
    }
    if (quickFilters.hotelsNearby && !hasNearbyHotels) {
      return false;
    }
    if (quickFilters.monthlyAvailable && location?.openForMonthly !== true) {
      return false;
    }
    if (quickFilters.monthlyOnly) {
      const isMonthlyOnly = location?.openForMonthly === true && location?.openForDaily !== true;
      if (!isMonthlyOnly) return false;
    }
    if (quickFilters.comingSoon && location?.openForParking === true) {
      return false;
    }
    return true;
  })
);

export const applyAvailabilityModeToLocations = (
  locations = [],
  availabilityMode = null,
  lotsAvailableByLocationId = {},
) => {
  if (availabilityMode !== LOCATION_AVAILABILITY_NOW) {
    return Array.isArray(locations) ? locations : [];
  }

  return (Array.isArray(locations) ? locations : []).filter((location) => {
    if (location?.openForParking !== true) return false;
    if (!location?.id) return false;

    const hasAvailabilitySnapshot = Object.prototype.hasOwnProperty.call(
      lotsAvailableByLocationId,
      location.id,
    );

    // While lots data is still hydrating, keep open locations visible.
    if (!hasAvailabilitySnapshot) {
      return true;
    }

    return getAvailableLotCount(location.id, lotsAvailableByLocationId) > 0;
  });
};

const getEffectiveDailyPrice = (location) => {
  const locationRates = getLocationRates(location);
  const baseDailyPrice = Number(locationRates?.daily);
  if (!Number.isFinite(baseDailyPrice)) return Number.POSITIVE_INFINITY;

  // Align low-to-high ranking with the daily price users actually see/pay.
  if (location?.isPromoActive) {
    const promoDiscount = getLocationPromoDiscount(location);
    const promoDailyPrice = Number(calculatePromoPrice(baseDailyPrice, promoDiscount));
    return Number.isFinite(promoDailyPrice) ? promoDailyPrice : baseDailyPrice;
  }

  return baseDailyPrice;
};

const toRadians = (value) => (value * Math.PI) / 180;

const getCoordinatesFromLocation = (location) => {
  const latitude = Number(location?.coordinates?.lat);
  const longitude = Number(location?.coordinates?.lng);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { lat: latitude, lng: longitude };
};

export const getDistanceMiles = (originCoordinates, targetCoordinates) => {
  const originLat = Number(originCoordinates?.lat);
  const originLng = Number(originCoordinates?.lng);
  const targetLat = Number(targetCoordinates?.lat);
  const targetLng = Number(targetCoordinates?.lng);

  if (
    !Number.isFinite(originLat)
    || !Number.isFinite(originLng)
    || !Number.isFinite(targetLat)
    || !Number.isFinite(targetLng)
  ) {
    return Number.POSITIVE_INFINITY;
  }

  const EARTH_RADIUS_MILES = 3958.8;
  const latDelta = toRadians(targetLat - originLat);
  const lngDelta = toRadians(targetLng - originLng);
  const originLatRadians = toRadians(originLat);
  const targetLatRadians = toRadians(targetLat);

  const a = (
    Math.sin(latDelta / 2) ** 2
    + Math.cos(originLatRadians) * Math.cos(targetLatRadians) * (Math.sin(lngDelta / 2) ** 2)
  );
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_MILES * c;
};

const getLocationDistanceMiles = (location, originCoordinates) => {
  const locationCoordinates = getCoordinatesFromLocation(location);
  if (!locationCoordinates) return Number.POSITIVE_INFINITY;
  return getDistanceMiles(originCoordinates, locationCoordinates);
};

export const sortLocationsForMode = (
  locations = [],
  sortMode = null,
  lotsAvailableByLocationId = {},
  userCoordinates = null,
) => {
  const source = Array.isArray(locations) ? locations : [];
  if (!sortMode) return source;

  if (sortMode === LOCATION_SORT_DISTANCE_ASC) {
    const originLat = Number(userCoordinates?.lat);
    const originLng = Number(userCoordinates?.lng);
    if (!Number.isFinite(originLat) || !Number.isFinite(originLng)) {
      return source;
    }
  }

  const sorted = [...source].sort((a, b) => {
    if (sortMode === LOCATION_SORT_PRICE_ASC) {
      const priceDelta = getEffectiveDailyPrice(a) - getEffectiveDailyPrice(b);
      if (priceDelta !== 0) return priceDelta;
    }

    if (sortMode === LOCATION_SORT_AVAILABILITY_DESC) {
      const aAvailableLots = getAvailableLotCount(a?.id, lotsAvailableByLocationId);
      const bAvailableLots = getAvailableLotCount(b?.id, lotsAvailableByLocationId);
      if (aAvailableLots !== bAvailableLots) return bAvailableLots - aAvailableLots;
    }

    if (sortMode === LOCATION_SORT_DISTANCE_ASC) {
      const aDistanceMiles = getLocationDistanceMiles(a, userCoordinates);
      const bDistanceMiles = getLocationDistanceMiles(b, userCoordinates);
      if (aDistanceMiles !== bDistanceMiles) return aDistanceMiles - bDistanceMiles;
    }

    const aTitle = (a?.title || a?.address || '').toString();
    const bTitle = (b?.title || b?.address || '').toString();
    const titleDelta = aTitle.localeCompare(bTitle);
    if (titleDelta !== 0) return titleDelta;

    return String(a?.id || '').localeCompare(String(b?.id || ''));
  });

  return sorted;
};

const getActiveFilterKeys = (quickFilters = {}) => (
  Object.entries(quickFilters)
    .filter(([, value]) => value === true)
    .map(([key]) => key)
);

const Home = () => {
  const navigate = useNavigate();
  const {
    locationId: legacyPricingLocationIdParam,
    stateSlug,
    citySlug,
    intentSlug,
  } = useParams();
  const location = useLocation();
  const pricingPathMeta = useMemo(
    () => parsePricingPath(location.pathname),
    [location.pathname],
  );
  const truckParkingRouteMeta = useMemo(
    () => parseTruckParkingPath(location.pathname),
    [location.pathname],
  );
  const locationId = pricingPathMeta.locationId || sanitizeLocationId(legacyPricingLocationIdParam);
  const normalizedPricingLocationId = sanitizeLocationId(locationId);
  const activeSectionKey = getSectionByPath(location.pathname)?.sectionKey || 'home';
  const isHomepageRoute = location.pathname === '/' && activeSectionKey === 'home';
  const isCompactHomeAdSlider = activeSectionKey === 'home' && process.env.REACT_APP_COMPACT_AD_SLIDER === 'true';
  const isPricingRoute = pricingPathMeta.isPricingRoute;
  const isTruckParkingRoute = truckParkingRouteMeta.isTruckParkingRoute;
  const isLocationsRoute = (
    isTruckParkingRoute
    || /^\/locations(?:\/[^/]+){0,3}$/i.test(location.pathname)
  );
  const routeStateFilter = useMemo(
    () => getLocationStateBySlug(stateSlug || ''),
    [stateSlug],
  );
  const shouldDefaultOpenDesktopHomeMapOnFirstRender = (
    typeof window !== 'undefined'
    && window.innerWidth >= DESKTOP_DEFAULT_OPEN_MAP_MIN_WIDTH
    && isHomepageRoute
  );
  const [showAddresses, setShowAddresses] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const shouldShowSectionAtmosphere = isPricingRoute || (
    ['locations', 'rates', 'pm_partners', 'about'].includes(activeSectionKey) && !selectedLocation?.id
  );
  const shouldShowHomeMotion = activeSectionKey === 'home' && !selectedLocation?.id;
  const shouldShowYardV2Hero = isCompactHomeAdSlider && !selectedLocation?.id;
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionTimeout, setTransitionTimeout] = useState(null); // Store timeout reference
  const introNavigateTimeoutRef = useRef(null); // Timeout for intro slide auto-navigation
  const didLogLandingRef = useRef(false); // Prevent duplicate landing logs
  const didSetLocationFromUrlRef = useRef(false); // Prevent re-setting location on effect reruns
  const wasPricingRouteRef = useRef(isPricingRoute);
  const { 
    mapCenter, 
    mapZoom, 
    updateMapForLocation,
    resetMap
  } = useMapState();
  
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [checkoutStatus, setCheckoutStatus] = useState(false);
  const [ratingLocationId, setRatingLocationId] = useState(null);
  const [activePricingPanel, setActivePricingPanel] = useState('rates');
  const [activeAmenitySlide, setActiveAmenitySlide] = useState(0);
  const [pricingPanelHeight, setPricingPanelHeight] = useState(0);
  const [ads, setAds] = useState([]);
  const [isAdsLoading, setIsAdsLoading] = useState(false);
  const [nextAvailableDates, setNextAvailableDates] = useState({}); // Store next available date per location
  const pricingSectionRef = useRef(null); // Ref for auto-scroll to pricing
  const pricingRatesPanelRef = useRef(null);
  const pricingAmenitiesPanelRef = useRef(null);
  const pricingPanelTouchStartRef = useRef(null);
  const pricingLocationSectionRef = useRef(null);
  const stateCardsSectionRef = useRef(null);
  const [shouldRestoreStateCardsScroll, setShouldRestoreStateCardsScroll] = useState(false);
  const mapPlaceholderRef = useRef(null);
  const hasActivatedMapRef = useRef(shouldDefaultOpenDesktopHomeMapOnFirstRender);
  const loggedMapPreviewImpressionsRef = useRef(new Set());
  const [isMapActivated, setIsMapActivated] = useState(shouldDefaultOpenDesktopHomeMapOnFirstRender);
  const isMapFocusMode = !selectedLocation && showAddresses;
  const shouldAutoloadPricingMap = process.env.REACT_APP_PRICING_AUTOLOAD_MAP === 'true';
  const shouldShowJourneyFilters = !selectedLocation && (activeSectionKey === 'home' || activeSectionKey === 'locations');
  const shouldHideLocationsJourneyPanel = isLocationsRoute && !selectedLocation && showAddresses;
  const shouldShowStateCardsPanel = !selectedLocation && !showAddresses && !isLocationsRoute;
  const shouldShowLocationsFiltersInTopCard = isLocationsRoute && !selectedLocation && !showAddresses;
  const {
    searchIntent,
    initialAvailabilityMode,
    initialSortMode,
    requireOpenForDaily,
    requireOpenForParking,
  } = useMemo(
    () => parseLocationsRouteIntent(location.search),
    [location.search],
  );
  const defaultQuickFiltersForRoute = useMemo(
    () => getDefaultQuickFilters(),
    [],
  );
  const [quickFilters, setQuickFilters] = useState(() => defaultQuickFiltersForRoute);
  const [availabilityMode, setAvailabilityMode] = useState(() => initialAvailabilityMode);
  const [sortMode, setSortMode] = useState(() => getDefaultSortMode(searchIntent, initialSortMode));
  const [userCoordinates, setUserCoordinates] = useState(null);
  const [isGeoLocating, setIsGeoLocating] = useState(false);
  const [geolocationError, setGeolocationError] = useState(null);
  const [isRefinementPanelOpen, setIsRefinementPanelOpen] = useState(false);
  const [isAmenitiesFiltersOpen, setIsAmenitiesFiltersOpen] = useState(false);
  const [isMobileFilterViewport, setIsMobileFilterViewport] = useState(() => (
    typeof window !== 'undefined' && window.innerWidth < 768
  ));
  const [isDesktopDefaultMapViewport, setIsDesktopDefaultMapViewport] = useState(() => (
    typeof window !== 'undefined' && window.innerWidth >= DESKTOP_DEFAULT_OPEN_MAP_MIN_WIDTH
  ));
  const hasLoggedUrgentHandoffRef = useRef(false);
  const lastAppliedRouteStateSlugRef = useRef(null);
  const isApplyingRouteStateFilterRef = useRef(false);
  const previousSectionKeyRef = useRef(activeSectionKey);
  const shouldDefaultOpenDesktopHomeMap = (
    isDesktopDefaultMapViewport
    && isHomepageRoute
    && !selectedLocation
  );
  const shouldForceDesktopHomeMapListVisible = shouldDefaultOpenDesktopHomeMap;

  const getStickyHeaderOffset = useCallback(() => {
    if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') return 0;

    const headerCandidates = Array.from(document.querySelectorAll('header, nav'));
    return headerCandidates.reduce((maxOffset, element) => {
      const styles = window.getComputedStyle(element);
      if (styles.position !== 'sticky' && styles.position !== 'fixed') return maxOffset;
      if (styles.display === 'none' || styles.visibility === 'hidden') return maxOffset;

      const rect = element.getBoundingClientRect();
      if (rect.height <= 0) return maxOffset;
      if (rect.bottom <= 0) return maxOffset;
      if (rect.top > 4) return maxOffset;
      return Math.max(maxOffset, rect.height);
    }, 0);
  }, []);

  const scrollElementToTopWithOffset = useCallback(
    (element) => {
      if (!element || typeof element.scrollIntoView !== 'function') return;

      if (typeof window === 'undefined') {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      const stickyOffset = getStickyHeaderOffset();
      const elementTop = element.getBoundingClientRect().top + window.scrollY;
      const targetTop = Math.max(0, Math.round(elementTop - stickyOffset - 8));

      if (typeof window.scrollTo === 'function') {
        try {
          window.scrollTo({ top: targetTop, behavior: 'smooth' });
          return;
        } catch (error) {
          // JSDOM and some browser contexts can reject smooth-scroll options.
        }
      }

      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    [getStickyHeaderOffset]
  );

  const {
    bookingDetails,
    setBookingDetails,
    showBookingModal,
    setShowBookingModal,
    setBookingSource,
    bookingSubmitted,
    setBookingSubmitted,
    filterLotsByLocationId,
    lotsAvailableByLocationId,
    lotsBookedByLocationId,
    filteredAddresses,
    addresses,
    isAddressesLoading = false,
    searchQuery,
    setSearchQuery,
    stateFilter,
    setStateFilter,
    getLotIdByNumber,
    pricingOptions,
    gateCode,
    setGateCode,
    message,
    setMessage,
    isInitialLotsLoading = false,
    isRefreshingLots = false,
    contentMap,
    setContent,
    content,
    seoContent,
    setTitle,
    defaultRates,
    retrieveLots = async () => null,
  } = useContext(AppContext);

  const isPricingHydrating = isPricingRoute && !!normalizedPricingLocationId && !selectedLocation && (isInitialLotsLoading || isAddressesLoading);
  const isPricingLocationNotFound = isPricingRoute && !!normalizedPricingLocationId && !selectedLocation && !isInitialLotsLoading && !isAddressesLoading;
  const [retiredLocationHandoff, setRetiredLocationHandoff] = useState(null);
  const [isRetiredLocationHandoffLoading, setIsRetiredLocationHandoffLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!isPricingLocationNotFound || !normalizedPricingLocationId) {
      setRetiredLocationHandoff(null);
      setIsRetiredLocationHandoffLoading(false);
      return undefined;
    }

    setIsRetiredLocationHandoffLoading(true);
    axios.get(`/api/public/location-handoffs/${encodeURIComponent(normalizedPricingLocationId)}`)
      .then((response) => {
        if (!cancelled) setRetiredLocationHandoff(response.data?.handoff || null);
      })
      .catch(() => {
        if (!cancelled) setRetiredLocationHandoff(null);
      })
      .finally(() => {
        if (!cancelled) setIsRetiredLocationHandoffLoading(false);
      });

    return () => { cancelled = true; };
  }, [isPricingLocationNotFound, normalizedPricingLocationId]);

  const historicalPricingLocation = retiredLocationHandoff?.location || null;
  const retiredLocationTenantName = retiredLocationHandoff?.tenantName || 'the location operator';
  const retiredLocationDestination = retiredLocationHandoff?.destination || '';

  const discoveryScopedAddresses = useMemo(
    () => (
      isTruckParkingRoute
        ? filterLocationsForTruckParkingRoute(filteredAddresses, truckParkingRouteMeta)
        : filteredAddresses
    ),
    [filteredAddresses, isTruckParkingRoute, truckParkingRouteMeta],
  );

  const locationsAnalyticsBase = useMemo(
    () => ({
      source: searchIntent === LOCATION_INTENT_LAST_HOUR_PARKING ? 'last_hour_parking' : 'locations',
      page: 'locations',
      path: `${location.pathname}${location.search || ''}`,
      intent: searchIntent || null,
      availabilityMode: availabilityMode || null,
      requireOpenForDaily,
      requireOpenForParking,
      hasUserCoordinates: Boolean(userCoordinates),
    }),
    [availabilityMode, location.pathname, location.search, requireOpenForDaily, requireOpenForParking, searchIntent, userCoordinates],
  );

  const requireOpenForDailyForRoute = requireOpenForDaily || !isLocationsRoute;

  useEffect(() => {
    if (!isLocationsRoute || typeof setStateFilter !== 'function') return;

    const normalizedStateSlug = String(stateSlug || '').toLowerCase();
    if (lastAppliedRouteStateSlugRef.current === normalizedStateSlug) return;

    const nextStateFilter = routeStateFilter?.code || null;
    lastAppliedRouteStateSlugRef.current = normalizedStateSlug;
    isApplyingRouteStateFilterRef.current = true;
    setStateFilter(nextStateFilter);
  }, [isLocationsRoute, routeStateFilter, setStateFilter, stateSlug]);

  useEffect(() => {
    if (!isLocationsRoute) {
      lastAppliedRouteStateSlugRef.current = null;
      isApplyingRouteStateFilterRef.current = false;
    }
  }, [isLocationsRoute]);

  useEffect(() => {
    if (!isTruckParkingRoute) return;
    if (truckParkingRouteMeta.routeType === 'hub') return;

    if (isApplyingRouteStateFilterRef.current) {
      isApplyingRouteStateFilterRef.current = false;
      return;
    }

    const nextPath = stateFilter
      ? getLocationStatePathByCode(stateFilter)
      : TRUCK_PARKING_BASE_PATH;

    if (!nextPath || nextPath === location.pathname) return;
    navigate(nextPath);
  }, [
    isTruckParkingRoute,
    location.pathname,
    navigate,
    stateFilter,
    truckParkingRouteMeta.routeType,
  ]);

  useEffect(() => {
    if (!isTruckParkingRoute) return;
    if (truckParkingRouteMeta.routeType !== 'city' && truckParkingRouteMeta.routeType !== 'intent') return;

    const normalizedSearchQuery = String(searchQuery || '').trim();
    if (!normalizedSearchQuery) return;

    const parentStatePath = buildTruckParkingCityPath({
      stateSlug: truckParkingRouteMeta.stateSlug,
      citySlug: '',
    });

    if (!parentStatePath || parentStatePath === location.pathname) return;
    navigate(parentStatePath);
  }, [
    isTruckParkingRoute,
    location.pathname,
    navigate,
    searchQuery,
    truckParkingRouteMeta.routeType,
    truckParkingRouteMeta.stateSlug,
  ]);

  useEffect(() => {
    if (!isLocationsRoute) {
      hasLoggedUrgentHandoffRef.current = false;
    }

    const routeDefaults = getRouteScopedDiscoveryUiDefaults({
      isLocationsRoute,
      searchIntent,
      initialSortMode,
      initialAvailabilityMode,
    });

    setQuickFilters(routeDefaults.quickFilters);
    setAvailabilityMode(routeDefaults.availabilityMode);
    setSortMode(routeDefaults.sortMode);
    setIsRefinementPanelOpen(false);
  }, [
    initialAvailabilityMode,
    initialSortMode,
    isLocationsRoute,
    location.search,
    searchIntent,
  ]);

  useEffect(() => {
    setIsAmenitiesFiltersOpen(false);
    setIsRefinementPanelOpen(false);
    setGeolocationError(null);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => {
      setIsMobileFilterViewport(window.innerWidth < 768);
      setIsDesktopDefaultMapViewport(window.innerWidth >= DESKTOP_DEFAULT_OPEN_MAP_MIN_WIDTH);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isLocationsRoute || searchIntent !== LOCATION_INTENT_LAST_HOUR_PARKING) {
      hasLoggedUrgentHandoffRef.current = false;
      return;
    }
    if (hasLoggedUrgentHandoffRef.current) return;

    hasLoggedUrgentHandoffRef.current = true;
    logEvent('last_hour_parking_handoff_completed', {
      ...locationsAnalyticsBase,
      source: 'last_hour_parking',
    });
  }, [isLocationsRoute, locationsAnalyticsBase, searchIntent]);

  const getLocationById = useCallback(
    (id) => {
      if (!id) return null;
      const normalizedId = String(id);
      return (
        addresses.find((addr) => String(addr?.id) === normalizedId)
        || filteredAddresses.find((addr) => String(addr?.id) === normalizedId)
        || null
      );
    },
    [addresses, filteredAddresses]
  );

  const getLocationByAddress = useCallback(
    (addressValue) => {
      if (!addressValue) return null;
      return (
        addresses.find((addr) => addr?.address === addressValue)
        || filteredAddresses.find((addr) => addr?.address === addressValue)
        || null
      );
    },
    [addresses, filteredAddresses]
  );

  const getPricingPathForLocation = useCallback(
    (locationOrId) => {
      const locationObject = (
        locationOrId && typeof locationOrId === 'object'
          ? locationOrId
          : getLocationById(locationOrId)
      );
      const canonicalPath = buildCanonicalPricingPath(locationObject);
      if (canonicalPath) return canonicalPath;

      const fallbackLocationId = sanitizeLocationId(
        locationObject?.id || locationObject?.locationId || locationOrId || '',
      );
      if (!fallbackLocationId) return null;
      return `/pricing/${encodeURIComponent(fallbackLocationId)}`;
    },
    [getLocationById]
  );

  const staticMapMarkerIconUrl = useMemo(() => resolveStaticMapMarkerIconUrl(), []);
  const staticMapMarkerDescriptorIconUrl = useMemo(
    () => encodeURIComponent(appendStaticMapMarkerVersion(staticMapMarkerIconUrl)),
    [staticMapMarkerIconUrl]
  );
  const mapPreviewUrl = useMemo(() => {
    const scopedLocations = historicalPricingLocation
      ? [historicalPricingLocation]
      : ((Array.isArray(discoveryScopedAddresses) && discoveryScopedAddresses.length > 0
        ? discoveryScopedAddresses
        : addresses
      ) || []);
    const allCoordinates = dedupeCoordinates(
      scopedLocations
      .map(getValidCoordinates)
      .filter(Boolean)
    );

    if (allCoordinates.length === 0) return null;

    const selectedCoordinates = getValidCoordinates(selectedLocation || historicalPricingLocation);
    const markerCoordinates = pickStaticPreviewMarkerCoordinates(
      allCoordinates,
      STATIC_MAP_MAX_MARKERS,
      selectedCoordinates
    );
    if (markerCoordinates.length === 0) return null;

    const viewportCoordinates = selectedCoordinates ? [selectedCoordinates] : allCoordinates;
    const viewportBounds = getCoordinateBounds(viewportCoordinates);

    const computedZoom = selectedCoordinates
      ? 13
      : computeStaticPreviewZoom(viewportBounds, {
        widthPx: STATIC_MAP_SIZE.width,
        heightPx: STATIC_MAP_SIZE.height,
        paddingPx: STATIC_MAP_PADDING_PX,
        minZoom: 3,
        maxZoom: 12,
      });
    const baseCenterCoordinates = selectedCoordinates || getBoundsCenter(viewportBounds) || getAverageCoordinates(allCoordinates);
    const centerCoordinates = (() => {
      if (!baseCenterCoordinates || selectedCoordinates || !viewportBounds) return baseCenterCoordinates;
      if (stateFilter) return baseCenterCoordinates;
      if (markerCoordinates.length <= 1) return baseCenterCoordinates;

      const markerBounds = getCoordinateBounds(markerCoordinates);
      if (!markerBounds) return baseCenterCoordinates;
      const staticViewportWidth = getStaticMapViewportWidth();
      const { targetRatio, maxWestwardShiftDegrees } = getResponsiveRightEdgeShiftConfig(staticViewportWidth);

      const visibleLngSpan = clamp(
        (STATIC_MAP_SIZE.width / (256 * (2 ** computedZoom))) * 360,
        0,
        360
      );
      const targetOffset = (targetRatio - 0.5) * visibleLngSpan;
      const desiredCenterLng = markerBounds.maxLng - targetOffset;
      const rawWestwardShift = baseCenterCoordinates.lng - desiredCenterLng;
      const westwardShift = clamp(rawWestwardShift, 0, maxWestwardShiftDegrees);

      if (!Number.isFinite(westwardShift) || westwardShift <= 0) return baseCenterCoordinates;

      return {
        ...baseCenterCoordinates,
        lng: normalizeLongitude(baseCenterCoordinates.lng - westwardShift),
      };
    })();
    if (!centerCoordinates) return null;

    const queryParts = [
      `size=${encodeURIComponent(`${STATIC_MAP_SIZE.width}x${STATIC_MAP_SIZE.height}`)}`,
      `scale=${encodeURIComponent(String(STATIC_MAP_SCALE))}`,
      `maptype=${encodeURIComponent('roadmap')}`,
      `zoom=${encodeURIComponent(String(computedZoom))}`,
      `center=${encodeURIComponent(`${centerCoordinates.lat},${centerCoordinates.lng}`)}`,
    ];

    markerCoordinates.forEach((coordinates) => {
      const markerDescriptor = staticMapMarkerDescriptorIconUrl
        ? `icon:${staticMapMarkerDescriptorIconUrl}|scale:${STATIC_MAP_MARKER_SCALE}|anchor:center|${coordinates.lat},${coordinates.lng}`
        : `size:mid|color:0xB8860B|${coordinates.lat},${coordinates.lng}`;
      queryParts.push(`markers=${markerDescriptor}`);
    });

    if (process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
      queryParts.push(`key=${encodeURIComponent(process.env.REACT_APP_GOOGLE_MAPS_API_KEY)}`);
    }

    return `https://maps.googleapis.com/maps/api/staticmap?${queryParts.join('&')}`;
  }, [addresses, discoveryScopedAddresses, historicalPricingLocation, selectedLocation, staticMapMarkerDescriptorIconUrl, stateFilter]);

  const hasLotsSnapshotForFilteredAddresses = useMemo(() => {
    if (!Array.isArray(discoveryScopedAddresses) || discoveryScopedAddresses.length === 0) return false;
    if (!lotsAvailableByLocationId || typeof lotsAvailableByLocationId !== 'object') return false;

    return discoveryScopedAddresses.every((location) => (
      location?.id && Object.prototype.hasOwnProperty.call(lotsAvailableByLocationId, location.id)
    ));
  }, [discoveryScopedAddresses, lotsAvailableByLocationId]);

  // Route is the source of truth for top-level section state.
  useEffect(() => {
    if (content === 'bookingConfirmation') return;

    const nextSection = getSectionByPath(location.pathname)?.sectionKey || 'home';
    if (content !== nextSection) {
      setContent(nextSection);
    }
  }, [content, location.pathname, setContent]);
  
  // Find next available dates for locations with no spots tonight
  useEffect(() => {
    let cancelled = false;

    const findNextDatesForLocations = async () => {
      // Guard against undefined or empty arrays
      if (!discoveryScopedAddresses || discoveryScopedAddresses.length === 0) return;
      if (isInitialLotsLoading) return;
      if (!hasLotsSnapshotForFilteredAddresses) return;
      
      const locationsNeedingNextDate = discoveryScopedAddresses.filter(addr => {
        const availableLots = lotsAvailableByLocationId[addr.id]?.length || 0;
        const hasFutureAvailability = addr.hasFutureAvailability !== undefined 
          ? addr.hasFutureAvailability 
          : (addr.openForParking && availableLots === 0);
        // Treat undefined as "not looked up yet"; null means looked up with no date found.
        return hasFutureAvailability
          && availableLots === 0
          && typeof nextAvailableDates[addr.id] === 'undefined';
      });
      
      if (locationsNeedingNextDate.length === 0) return;
      
      const today = todayEST();
      const nextDates = {};
      const locationIds = locationsNeedingNextDate
        .map((location) => location?.id)
        .filter(Boolean);

      try {
        const {
          findNextAvailableDate,
          findNextAvailableDatesBatch,
        } = await import('../utils/findNextAvailableDate');
        if (cancelled) return;

        const batchResults = await findNextAvailableDatesBatch(locationIds, today, 30);
        if (cancelled) return;
        locationIds.forEach((locationId) => {
          const result = batchResults?.[locationId];
          nextDates[locationId] = (result?.hasAvailability && result?.date) ? result.date : null;
        });
      } catch (_batchError) {
        if (cancelled) return;
        const { findNextAvailableDate } = await import('../utils/findNextAvailableDate');
        if (cancelled) return;

        // Fallback to per-location lookups if batch endpoint is unavailable.
        await Promise.all(
          locationIds.map(async (locationId) => {
            try {
              const result = await findNextAvailableDate(locationId, today, 30);
              nextDates[locationId] = (result?.hasAvailability && result?.date) ? result.date : null;
            } catch (error) {
              console.error(`Failed to find next date for ${locationId}:`, error);
              nextDates[locationId] = null;
            }
          })
        );
      }
      
      if (!cancelled && Object.keys(nextDates).length > 0) {
        setNextAvailableDates(prev => ({ ...prev, ...nextDates }));
      }
    };
    
    findNextDatesForLocations();
    return () => {
      cancelled = true;
    };
  }, [discoveryScopedAddresses, hasLotsSnapshotForFilteredAddresses, isInitialLotsLoading, lotsAvailableByLocationId, nextAvailableDates]);

  // Handle direct links and URL parameters
  useEffect(() => {
    const locationIdFromUrl = parseLocationFromUrl();
    const isGoogleBusinessEntry = location.pathname === '/' && locationIdFromUrl;
    const isHomeEntry = location.pathname === '/';
    // Helper to determine traffic source from URL params
    const getTrafficSource = (params) => {
      const gbp = params.get('gbp') || '';
      const qrSource = params.get('source') || '';
      const utmSource = params.get('utm_source') || '';
      const utmCampaign = params.get('utm_campaign') || '';
      const utmContent = params.get('utm_content') || '';
      const messageType = params.get('messageType') || '';
      const emailType = params.get('emailType') || '';
      const checkoutMode = params.get('checkoutMode') || '';
      const ctaId = params.get('ctaId') || '';
      const normalizedUtmSource = utmSource.trim().toLowerCase();
      const isGoogleMapsUtmSource = ['google_maps', 'google_business_profile'].includes(normalizedUtmSource);
      
      // Email traffic takes priority if utm_source=email
      if (normalizedUtmSource === 'email') {
        return {
          source: 'email',
          signalType: 'landing_entry',
          emailCampaign: utmCampaign || undefined,
          emailAction: utmContent || undefined,
          emailMessageType: messageType || undefined,
          emailType: emailType || undefined,
          checkoutMode: checkoutMode || undefined,
          ctaId: ctaId || undefined,
        };
      }
      if (qrSource === 'qr') return { source: 'qr_scan' };
      if (isGoogleMapsUtmSource) return { source: 'google_maps' };
      if (gbp) return { source: 'google_maps' };
      return { source: 'rstc_app' };
    };

    // Log landing on home once: Email vs QR vs GBP vs Direct
    if (!didLogLandingRef.current && isHomeEntry && !isGoogleBusinessEntry) {
      try {
        const params = new URLSearchParams(window.location.search);
        const path = `${window.location.pathname}${window.location.search}`;
        const trafficInfo = getTrafficSource(params);
        logEvent('landing_entry', {
          ...trafficInfo,
          locationId: locationIdFromUrl || undefined,
          path,
        });
        didLogLandingRef.current = true;
      } catch (e) {}
    }
    // Log landing immediately once
    if (isGoogleBusinessEntry && !didLogLandingRef.current) {
      try {
        const params = new URLSearchParams(window.location.search);
        const path = `${window.location.pathname}${window.location.search}`;
        const trafficInfo = getTrafficSource(params);
        logEvent('landing_entry', {
          ...trafficInfo,
          locationId: locationIdFromUrl,
          path,
        });
        didLogLandingRef.current = true;
      } catch (e) {}
    }

    // Log landing on pricing route as well (deep links): Email vs QR vs GBP vs Direct
    if (!didLogLandingRef.current && window.location.pathname.startsWith('/pricing/')) {
      try {
        const params = new URLSearchParams(window.location.search);
        const path = `${window.location.pathname}${window.location.search}`;
        const trafficInfo = getTrafficSource(params);
        logEvent('landing_entry', {
          ...trafficInfo,
          // Sanitize locationId in case URL was malformed (e.g., /pricing/location123&gbp=1)
          locationId: sanitizeLocationId(locationId) || undefined,
          path,
        });
        didLogLandingRef.current = true;
      } catch (e) {}
    }
    
    // Set selected location as soon as filteredAddresses is available (don't wait for lots data)
    if (locationIdFromUrl && addresses.length > 0 && !didSetLocationFromUrlRef.current) {
      const matchedLocation = getLocationById(locationIdFromUrl);
      if (matchedLocation) {
        didSetLocationFromUrlRef.current = true;
        setSelectedLocation(matchedLocation);
        setSelectedAddress(matchedLocation.address);
        setGateCode(matchedLocation.gateCode || '0000');
        updateMapForLocation(matchedLocation.coordinates);
        setTitle(matchedLocation.address);
        
        // Non-Google entry: navigate to locations immediately
        if (!isGoogleBusinessEntry) {
          navigate(TRUCK_PARKING_BASE_PATH, { replace: true });
        }
      }
    }
    
    // Google Business entry: start 6s timer once lots data is ready
    if (isGoogleBusinessEntry && locationIdFromUrl && Object.keys(lotsAvailableByLocationId).length > 0 && !introNavigateTimeoutRef.current) {
      introNavigateTimeoutRef.current = setTimeout(() => {
        introNavigateTimeoutRef.current = null;
        const pricingPath = getPricingPathForLocation(locationIdFromUrl) || `/pricing/${locationIdFromUrl}`;
        navigate(pricingPath, { replace: true });
      }, 6000);
    }
  }, [addresses, getLocationById, getPricingPathForLocation, lotsAvailableByLocationId, navigate, setGateCode, setTitle, updateMapForLocation, location.pathname]);

  useEffect(() => {
    if (!isPricingRoute || !selectedLocation) return;
    const selectedLocationId = sanitizeLocationId(selectedLocation.id);
    // During pricing->pricing navigation the route id can update before selectedLocation
    // is hydrated for the new route. Skip canonical rewrites until ids are aligned.
    if (
      normalizedPricingLocationId
      && selectedLocationId
      && selectedLocationId !== normalizedPricingLocationId
    ) {
      return;
    }
    if (isCanonicalPricingPath(location.pathname, selectedLocation)) return;

    const canonicalPricingPath = getPricingPathForLocation(selectedLocation);
    if (!canonicalPricingPath) return;

    const querySuffix = location.search || '';
    navigate(`${canonicalPricingPath}${querySuffix}`, { replace: true });
  }, [
    getPricingPathForLocation,
    isPricingRoute,
    location.pathname,
    location.search,
    navigate,
    normalizedPricingLocationId,
    selectedLocation,
  ]);

  // Clean up transition timeouts when component unmounts
  useEffect(() => {
    return () => {
      if (transitionTimeout) {
        clearTimeout(transitionTimeout);
      }
      if (introNavigateTimeoutRef.current) {
        clearTimeout(introNavigateTimeoutRef.current);
        introNavigateTimeoutRef.current = null;
      }
    };
  }, [transitionTimeout]);

  const eligibleLocations = useMemo(
    () => (
      (Array.isArray(discoveryScopedAddresses) ? discoveryScopedAddresses : []).filter((location) => (
        (
          requireOpenForDaily
            ? location?.openForDaily === true
            : (!requireOpenForDailyForRoute || location?.openForDaily !== false)
        )
        && (!requireOpenForParking || location?.openForParking === true)
        && isLocationEligibleForResults(
          location,
          lotsAvailableByLocationId,
          lotsBookedByLocationId,
          {
            includeMonthlyWithoutLotInventory: isLocationsRoute && !requireOpenForDailyForRoute,
          }
        )
      ))
    ),
    [discoveryScopedAddresses, isLocationsRoute, lotsAvailableByLocationId, lotsBookedByLocationId, requireOpenForDaily, requireOpenForParking],
  );

  const availabilityScopedLocations = useMemo(() => {
    return applyAvailabilityModeToLocations(
      eligibleLocations,
      availabilityMode,
      lotsAvailableByLocationId,
    );
  }, [availabilityMode, eligibleLocations, lotsAvailableByLocationId]);

  const quickFilteredLocations = useMemo(
    () => applyQuickFiltersToLocations(availabilityScopedLocations, quickFilters),
    [availabilityScopedLocations, quickFilters],
  );

  const effectiveSortMode = sortMode || null;

  const locationsForResults = useMemo(() => {
    const fallbackOrder = [...quickFilteredLocations].sort((a, b) => {
      if (a.isPromoActive && !b.isPromoActive) return -1;
      if (!a.isPromoActive && b.isPromoActive) return 1;
      return 0;
    });

    if (!effectiveSortMode) {
      return fallbackOrder;
    }

    return sortLocationsForMode(
      quickFilteredLocations,
      effectiveSortMode,
      lotsAvailableByLocationId,
      userCoordinates,
    );
  }, [effectiveSortMode, quickFilteredLocations, lotsAvailableByLocationId, userCoordinates]);

  const resultRankByLocationId = useMemo(() => {
    const map = new Map();
    locationsForResults.forEach((result, index) => {
      map.set(String(result?.id || ''), index);
    });
    return map;
  }, [locationsForResults]);

  const distanceMilesByLocationId = useMemo(() => {
    const byLocationId = new Map();
    if (!userCoordinates) return byLocationId;

    locationsForResults.forEach((location) => {
      const distanceMiles = getLocationDistanceMiles(location, userCoordinates);
      if (Number.isFinite(distanceMiles)) {
        byLocationId.set(String(location?.id || ''), Number(distanceMiles.toFixed(1)));
      }
    });

    return byLocationId;
  }, [locationsForResults, userCoordinates]);

  const activeResultFilters = useMemo(
    () => getActiveFilterKeys(quickFilters),
    [quickFilters],
  );

  const promotedRefinementKeys = useMemo(
    () => ['securityCameras', 'pavedSurface', 'gated'],
    [],
  );

  const promotedRefinementFilters = useMemo(
    () => LOCATION_REFINEMENT_FILTERS.filter((option) => promotedRefinementKeys.includes(option.key)),
    [promotedRefinementKeys],
  );

  const hiddenRefinementFilters = useMemo(
    () => LOCATION_REFINEMENT_FILTERS.filter((option) => !promotedRefinementKeys.includes(option.key)),
    [promotedRefinementKeys],
  );

  const activeRefinementCount = useMemo(
    () => LOCATION_REFINEMENT_FILTERS
      .filter((option) => quickFilters[option.key] === true)
      .length,
    [quickFilters],
  );

  const hasNonDefaultFilters = useMemo(
    () => Object.keys(defaultQuickFiltersForRoute).some((key) => (
      Boolean(quickFilters[key]) !== Boolean(defaultQuickFiltersForRoute[key])
    )),
    [defaultQuickFiltersForRoute, quickFilters],
  );

  const isBaseTruckParkingRoute = location.pathname.replace(/\/+$/, '') === TRUCK_PARKING_BASE_PATH;
  const shouldShowNetworkRebuildFlow = (
    isBaseTruckParkingRoute
    && !selectedLocation
    && locationsForResults.length === 0
    && !hasNonDefaultFilters
    && !String(searchQuery || '').trim()
    && !stateFilter
  );

  const logFilterChange = useCallback(
    (filterKey, filterValue, nextFilters) => {
      const resultsCount = applyQuickFiltersToLocations(
        availabilityScopedLocations,
        nextFilters,
      ).length;

      logEvent('locations_filter_changed', {
        ...locationsAnalyticsBase,
        filterKey,
        filterValue,
        intent: searchIntent || null,
        resultsCount,
      });
    },
    [availabilityScopedLocations, locationsAnalyticsBase, searchIntent],
  );

  const handleQuickFilterToggle = useCallback(
    (filterKey) => {
      setQuickFilters((previous) => {
        const next = {
          ...previous,
          [filterKey]: !previous[filterKey],
        };
        logFilterChange(filterKey, next[filterKey], next);
        return next;
      });
    },
    [logFilterChange],
  );

  const handleClearQuickFilters = useCallback(() => {
    setQuickFilters((previous) => {
      const next = { ...defaultQuickFiltersForRoute };
      Object.keys(next).forEach((filterKey) => {
        if (Boolean(previous[filterKey]) !== Boolean(next[filterKey])) {
          logFilterChange(filterKey, next[filterKey], next);
        }
      });
      return next;
    });
  }, [defaultQuickFiltersForRoute, logFilterChange]);

  const handleClearNoResultsFilters = useCallback(() => {
    if (typeof setSearchQuery === 'function') {
      setSearchQuery('');
    }
    if (typeof setStateFilter === 'function') {
      setStateFilter(null);
    }

    setQuickFilters(getDefaultQuickFilters());
    setAvailabilityMode(null);
    setSortMode(null);
    setGeolocationError(null);
    setIsRefinementPanelOpen(false);
    setIsAmenitiesFiltersOpen(false);
  }, [setSearchQuery, setStateFilter]);

  const requestCurrentCoordinates = useCallback(() => new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window?.navigator?.geolocation) {
      reject(new Error('Geolocation is not supported by this browser.'));
      return;
    }

    window.navigator.geolocation.getCurrentPosition(
      (position) => {
        const latitude = Number(position?.coords?.latitude);
        const longitude = Number(position?.coords?.longitude);
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
          reject(new Error('Unable to resolve your location coordinates.'));
          return;
        }
        resolve({ lat: latitude, lng: longitude });
      },
      (error) => {
        reject(error || new Error('Unable to access your location.'));
      },
      {
        enableHighAccuracy: true,
        maximumAge: 5 * 60 * 1000,
        timeout: 8000,
      },
    );
  }), []);

  const getGeolocationFailureReason = useCallback((error) => {
    const permissionDeniedCode = 1;
    const timeoutCode = 3;
    const code = Number(error?.code);
    if (code === permissionDeniedCode) return 'permission_denied';
    if (code === timeoutCode) return 'timeout';
    if (typeof error?.message === 'string' && error.message.trim().length > 0) {
      return error.message;
    }
    return 'unknown';
  }, []);

  const handleSortModeChange = useCallback(
    async (nextSortMode) => {
      const normalizedSortMode = nextSortMode || null;

      if (normalizedSortMode === LOCATION_SORT_DISTANCE_ASC) {
        if (!userCoordinates) {
          setIsGeoLocating(true);
          setGeolocationError(null);

          try {
            const coordinates = await requestCurrentCoordinates();
            setUserCoordinates(coordinates);
            logEvent('locations_geolocation_resolved', {
              ...locationsAnalyticsBase,
            });
          } catch (error) {
            const failureReason = getGeolocationFailureReason(error);
            setGeolocationError(failureReason);
            setSortMode((currentSortMode) => (
              currentSortMode === LOCATION_SORT_DISTANCE_ASC ? null : currentSortMode
            ));
            logEvent('locations_geolocation_failed', {
              ...locationsAnalyticsBase,
              reason: failureReason,
            });
            return;
          } finally {
            setIsGeoLocating(false);
          }
        }
      } else {
        setGeolocationError(null);
      }

      setSortMode(normalizedSortMode);

      logEvent('locations_sort_changed', {
        ...locationsAnalyticsBase,
        sortMode: normalizedSortMode || 'default',
        intent: searchIntent || null,
        resultsCount: quickFilteredLocations.length,
      });
    },
    [
      getGeolocationFailureReason,
      locationsAnalyticsBase,
      quickFilteredLocations.length,
      requestCurrentCoordinates,
      searchIntent,
      userCoordinates,
    ],
  );

  useEffect(() => {
    if (!isLocationsRoute) return;
    if (sortMode !== LOCATION_SORT_DISTANCE_ASC) return;
    if (userCoordinates || isGeoLocating || geolocationError) return;

    void handleSortModeChange(LOCATION_SORT_DISTANCE_ASC);
  }, [
    geolocationError,
    handleSortModeChange,
    isGeoLocating,
    isLocationsRoute,
    sortMode,
    userCoordinates,
  ]);

  // Handle location selection with improved transition
  const handleStateCardClick = ({ locationId, source = 'state_card', rankIndex = null }) => {
    const matchedLocation = getLocationById(locationId) || null;
    if (matchedLocation) {
      const normalizedLocationId = String(locationId || '');
      const computedRankIndex = Number.isFinite(rankIndex)
        ? rankIndex
        : (resultRankByLocationId.get(normalizedLocationId) ?? null);
      const availableLots = getAvailableLotCount(locationId, lotsAvailableByLocationId);
      const distanceMiles = distanceMilesByLocationId.has(normalizedLocationId)
        ? distanceMilesByLocationId.get(normalizedLocationId)
        : null;

      logEvent('location_card_opened', {
        ...locationsAnalyticsBase,
        source,
        locationId,
        locationTitle: matchedLocation.title || matchedLocation.address,
        rankIndex: computedRankIndex,
        sortMode: effectiveSortMode || 'default',
        distanceMiles,
      });

      logEvent('locations_result_clicked', {
        ...locationsAnalyticsBase,
        source,
        intent: searchIntent || null,
        locationId,
        locationTitle: matchedLocation.title || matchedLocation.address,
        rankIndex: computedRankIndex,
        sortMode: effectiveSortMode || 'default',
        activeFilters: activeResultFilters,
        availableLots,
        openForParking: matchedLocation.openForParking === true,
        distanceMiles,
      });

      // Start transition - show skeletons
      setIsTransitioning(true);
      
      // Clear any existing timeouts
      if (transitionTimeout) {
        clearTimeout(transitionTimeout);
      }
      
      // First update internal state
      setSelectedLocation(matchedLocation);
      setSelectedAddress(matchedLocation.address);
      setGateCode(matchedLocation.gateCode || '0000');
      updateMapForLocation(matchedLocation.coordinates);
      setTitle(matchedLocation.address);
      
      // Then navigate with a slight delay and remove transition state
      const timeout = setTimeout(() => {
        const pricingPath = getPricingPathForLocation(matchedLocation) || `/pricing/${locationId}`;
        navigate(pricingPath, {
          state: {
            bookingSource: source,
          },
        });
        // Give a moment for the new view to render before removing transition state
        setTimeout(() => {
          setIsTransitioning(false);
        }, 100);
      }, 100);
      
      setTransitionTimeout(timeout);
    }
  };

  // Build amenities map from addresses from database (flags + nearby lists)
  // Uses addresses from AppContext which are fetched from /api/addresses (MongoDB)
  const amenitiesFromConfigById = React.useMemo(() => {
    const map = {};
    addresses.forEach((loc) => {
      // Get flags from database, defaulting to empty object if not present
      const flags = loc.flags || {};
      
      // Dynamically include ALL flags from the database
      // This ensures new amenity categories added to the database will be displayed
      const allFlags = {};
      Object.keys(flags).forEach(key => {
        allFlags[key] = !!flags[key];
      });
      
      map[loc.id] = {
        ...allFlags, // Include all flags dynamically from database
        nearbyGasStations: Array.isArray(loc.nearbyGasStations) ? loc.nearbyGasStations : [],
        nearbyHotels: Array.isArray(loc.nearbyHotels) ? loc.nearbyHotels : [],
        nearbyFood: Array.isArray(loc.nearbyFood) ? loc.nearbyFood : [],
        services: Array.isArray(loc.services) ? loc.services : []
      };
    });
    return map;
  }, [addresses]);

  useEffect(() => {
    setActivePricingPanel('rates');
    setActiveAmenitySlide(0);
  }, [selectedLocation?.id]);

  const pricingAmenityGroups = useMemo(
    () => buildPricingAmenityGroups(
      selectedLocation?.id ? amenitiesFromConfigById[selectedLocation.id] || {} : {}
    ),
    [amenitiesFromConfigById, selectedLocation?.id]
  );
  const hasPricingAmenities = pricingAmenityGroups.length > 0;

  const showPricingPanel = useCallback((panel) => {
    setActivePricingPanel(panel);
  }, []);

  const handlePricingPanelTouchStart = useCallback((event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    pricingPanelTouchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
    };
  }, []);

  const handlePricingPanelTouchEnd = useCallback((event) => {
    const touchStart = pricingPanelTouchStartRef.current;
    pricingPanelTouchStartRef.current = null;

    if (!touchStart || !hasPricingAmenities) return;

    const touch = event.changedTouches?.[0];
    if (!touch) return;

    const deltaX = touch.clientX - touchStart.x;
    const deltaY = touch.clientY - touchStart.y;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (absX < PRICING_PANEL_SWIPE_THRESHOLD_PX || absX <= absY * 1.25) return;

    if (deltaX < 0 && activePricingPanel === 'rates') {
      showPricingPanel('amenities');
      return;
    }

    if (deltaX > 0 && activePricingPanel === 'amenities') {
      showPricingPanel('rates');
    }
  }, [activePricingPanel, hasPricingAmenities, showPricingPanel]);

  useLayoutEffect(() => {
    const ratesHeight = Math.ceil(pricingRatesPanelRef.current?.getBoundingClientRect().height || 0);
    const amenitiesHeight = Math.ceil(pricingAmenitiesPanelRef.current?.getBoundingClientRect().height || 0);
    const nextHeight = Math.max(ratesHeight, amenitiesHeight);
    if (nextHeight > 0) {
      setPricingPanelHeight(nextHeight + 34);
    }
  }, [activePricingPanel, selectedLocation?.id, isMobileFilterViewport]);

  // Handle back button to return from pricing to the original home/state-card view.
  const handleBackToStatesClick = () => {
    // Start transition - show skeletons
    setIsTransitioning(true);

    // Clear any existing timeouts
    if (transitionTimeout) {
      clearTimeout(transitionTimeout);
    }
    if (introNavigateTimeoutRef.current) {
      clearTimeout(introNavigateTimeoutRef.current);
      introNavigateTimeoutRef.current = null;
    }

    // Set a timeout to handle the view change
    const timeout = setTimeout(() => {
      setSelectedLocation(null);
      setSelectedAddress(null);
      setGateCode('0000');
      resetMap();
      setTitle('Secure & Flexible Parking');

      // Navigate back to wherever the user came from.
      navigate(-1);

      // Give a moment for the new view to render before removing transition state
      setTimeout(() => {
        setIsTransitioning(false);
      }, 100);
    }, 100);

    setTransitionTimeout(timeout);
  };

  // Keep selected location in sync with pricing route.
  useEffect(() => {
    if (isPricingRoute && normalizedPricingLocationId) {
      const matchedLocation = getLocationById(normalizedPricingLocationId);
      if (matchedLocation) {
        setSelectedLocation(matchedLocation);
        setSelectedAddress(matchedLocation.address);
        setGateCode(matchedLocation.gateCode || '0000');
        updateMapForLocation(matchedLocation.coordinates);
        setTitle(matchedLocation.address);
      }
      return;
    }

  }, [
    isPricingRoute,
    normalizedPricingLocationId,
    getLocationById,
    setGateCode,
    setTitle,
    updateMapForLocation,
  ]);

  // Leaving pricing route should restore section-mode state.
  useEffect(() => {
    // Always reset when navigating back from pricing, even if selectedLocation
    // was not populated by the entry flow that opened pricing.
    if (wasPricingRouteRef.current && !isPricingRoute) {
      setSelectedLocation(null);
      setSelectedAddress(null);
      setGateCode('0000');
      resetMap();
      setTitle('Secure & Flexible Parking');
    }

    wasPricingRouteRef.current = isPricingRoute;
  }, [isPricingRoute, resetMap, setGateCode, setTitle]);

  // Warm the booking modal chunk while user is browsing pricing cards.
  useEffect(() => {
    if (!isPricingRoute || !selectedLocation) return;

    preloadBookingModal().catch(() => {
      // Best effort prefetch: no-op on failure.
    });

    if (typeof prewarmHotToast === 'function') {
      prewarmHotToast();
    }
  }, [isPricingRoute, selectedLocation]);

  const currentSEO = seoContent[activeSectionKey] || seoContent.home;
  const SelectedContent = contentMap[activeSectionKey] || contentMap.home;

  // Load ads from backend API (filtered by selected location if any)
  useEffect(() => {
    let isMounted = true;
    const loadAds = async () => {
      try {
        setIsAdsLoading(true);
        // Pass locationId to fetchAds to get location-specific ads + global ads
        const locationId = selectedLocation?.id || null;
        const apiAds = await fetchAds(locationId);
        if (isMounted) setAds(apiAds);
      } catch (e) {
        // ignore errors, will show empty list
        if (isMounted) setAds([]);
      } finally {
        if (isMounted) setIsAdsLoading(false);
      }
    };
    loadAds();
    return () => { isMounted = false; };
  }, [selectedLocation?.id]);

  // Build final ad list: promo(s) first, then backend ads from database
  const adItems = React.useMemo(() => {
    const hiringCreativeItems = featureFlags.publicHiringEnabled ? [
      {
        id: 'house-hiring-otr',
        imageSrc: '/tph-logo-desktop.png',
        alt: 'We hire OTR drivers',
        title: 'We Hire OTR Drivers',
        description: 'Apply in minutes with recruiter follow-up and guided compliance screening.',
        href: '/hiring/otr?source=home-ad-slider',
        learnMore: true,
        rounded: false,
        isPromo: false,
      },
    ] : [];

    // Map database ads to AdSlider format
    // Use title as id for analytics readability (fallback to _id if no title)
    const apiItems = (Array.isArray(ads) ? ads : []).map((a, idx) => ({
      id: a.title || a._id || `api-${idx}`,
      imageSrc: a.imageSrc || '/tph-logo-desktop.png',
      alt: a.title || 'Ad',
      title: a.title || '',
      description: a.description || '',
      href: a.href || '',
      learnMore: a.enableLink !== false && !!a.href, // Show "Learn more" if enableLink is true and href exists
      locationId: (a.enableBooking && Array.isArray(a.locations) && a.locations.length > 0) ? a.locations[0] : undefined,
      ctaLabel: (a.enableBooking && Array.isArray(a.locations) && a.locations.length > 0) ? 'Book Now' : undefined,
      rounded: !!a.rounded,
      isPromo: false,
    }));

    // Promo items from locations (dynamic, based on isPromoActive flag)
    const promoItems = (Array.isArray(discoveryScopedAddresses) ? discoveryScopedAddresses : [])
      .filter(loc => !!loc.isPromoActive)
      .slice(0, 3)
      .map((loc) => {
        const effectiveDailyPrice = getEffectiveDailyPrice(loc);
        const priceLabel = Number.isFinite(effectiveDailyPrice)
          ? `$${Number.isInteger(effectiveDailyPrice) ? effectiveDailyPrice : effectiveDailyPrice.toFixed(2)}/night`
          : null;
        const availableLotCount = getAvailableLotCount(loc.id, lotsAvailableByLocationId);
        const availabilityLabel = availableLotCount > 0
          ? `${availableLotCount} spot${availableLotCount === 1 ? '' : 's'} open`
          : 'Limited spots';
        const addressParts = parseAddressParts(loc.address || '');
        const marketLabel = [
          loc.city || addressParts.city,
          loc.state || addressParts.stateCode,
        ].filter(Boolean).join(', ');
        const description = [marketLabel, priceLabel, availabilityLabel].filter(Boolean).join(' · ');

        return {
          id: `promo-${loc.id}`,
          imageSrc: '/tph-logo-desktop.png',
          alt: `Promo ${loc.title || loc.address}`,
          title: loc.title || loc.address,
          description,
          href: getPricingPathForLocation(loc) || `/pricing/${loc.id}`,
          isPromo: true,
          promoDiscountLabel: getPromoDiscountLabel(loc),
          locationId: loc.id,
          ctaLabel: 'Book Now',
          learnMore: false,
          rounded: false,
        };
      });

    // Hide promo for currently selected location to avoid redundancy
    const filteredPromo = promoItems.filter(p => p.locationId !== selectedLocation?.id);
    
    // Combine: promos first, then hiring house creative, then database ads (sorted by priority in backend)
    const combined = [...filteredPromo, ...hiringCreativeItems, ...apiItems];
    return combined;
  }, [ads, discoveryScopedAddresses, getPricingPathForLocation, lotsAvailableByLocationId, selectedLocation?.id]);

  const heroOpenSpotsTonight = React.useMemo(() => {
    if (!Array.isArray(discoveryScopedAddresses)) return 0;

    return discoveryScopedAddresses.reduce((total, loc) => (
      total + getAvailableLotCount(loc?.id, lotsAvailableByLocationId)
    ), 0);
  }, [discoveryScopedAddresses, lotsAvailableByLocationId]);

  const heroLocationCount = locationsForResults.length;

  const handleBrowseCurrentLocations = useCallback(() => {
    if (stateCardsSectionRef.current) {
      scrollElementToTopWithOffset(stateCardsSectionRef.current);
    }
  }, [scrollElementToTopWithOffset]);

  const addressCardsRef = useRef(null);

  const activateMap = useCallback(() => {
    if (hasActivatedMapRef.current) return;
    hasActivatedMapRef.current = true;
    setIsMapActivated(true);
  }, []);

  const deactivateMap = useCallback(() => {
    hasActivatedMapRef.current = false;
    setIsMapActivated(false);
  }, []);

  useEffect(() => {
    if (!shouldAutoloadPricingMap) return;
    if (isMapActivated) return;
    if (isPricingRoute) {
      activateMap();
    }
  }, [activateMap, isMapActivated, isPricingRoute, shouldAutoloadPricingMap]);

  useEffect(() => {
    if (!historicalPricingLocation) return;
    activateMap();
  }, [activateMap, historicalPricingLocation]);

  useEffect(() => {
    if (!shouldDefaultOpenDesktopHomeMap) return;
    activateMap();
  }, [activateMap, shouldDefaultOpenDesktopHomeMap]);

  useEffect(() => {
    const previousSectionKey = previousSectionKeyRef.current;
    previousSectionKeyRef.current = activeSectionKey;

    if (selectedLocation) return;

    const movedBetweenHomeAndLocations =
      (previousSectionKey === 'home' && activeSectionKey === 'locations')
      || (previousSectionKey === 'locations' && activeSectionKey === 'home');

    if (!movedBetweenHomeAndLocations) return;

    if (shouldDefaultOpenDesktopHomeMap) {
      setShowAddresses(false);
      activateMap();
    } else {
      setShowAddresses(false);
      if (!isPricingRoute) {
        deactivateMap();
      }
    }
    setSelectedAddress(null);
    setShouldRestoreStateCardsScroll(false);
  }, [
    activateMap,
    activeSectionKey,
    deactivateMap,
    isPricingRoute,
    selectedLocation,
    shouldDefaultOpenDesktopHomeMap,
  ]);

  useEffect(() => {
    // In autoload pricing mode the placeholder may only flash briefly; skip intent impression logging.
    if (shouldAutoloadPricingMap && isPricingRoute) return;
    if (shouldDefaultOpenDesktopHomeMap) return;
    if (isMapActivated) return;

    const scopedLocationId = normalizedPricingLocationId || selectedLocation?.id || null;
    const impressionKey = `${location.pathname}|${activeSectionKey}|${scopedLocationId || 'none'}`;
    if (loggedMapPreviewImpressionsRef.current.has(impressionKey)) return;
    loggedMapPreviewImpressionsRef.current.add(impressionKey);

    logEvent('map_preview_impression', {
      source: 'map_placeholder',
      section: activeSectionKey,
      routePath: location.pathname,
      isPricingRoute,
      locationId: scopedLocationId || undefined,
      hasSelectedLocation: !!selectedLocation,
      mapActivationMode: shouldAutoloadPricingMap ? 'auto' : 'intent',
    });
  }, [
    activeSectionKey,
    isMapActivated,
    isPricingRoute,
    location.pathname,
    normalizedPricingLocationId,
    selectedLocation,
    shouldDefaultOpenDesktopHomeMap,
    shouldAutoloadPricingMap,
  ]);

  useEffect(() => {
    if (showAddresses || selectedLocation || !shouldRestoreStateCardsScroll) return;
    if (!stateCardsSectionRef.current) return;

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => {
        scrollElementToTopWithOffset(stateCardsSectionRef.current);
        setShouldRestoreStateCardsScroll(false);
      });
      return;
    }

    scrollElementToTopWithOffset(stateCardsSectionRef.current);
    setShouldRestoreStateCardsScroll(false);
  }, [
    scrollElementToTopWithOffset,
    selectedLocation,
    shouldRestoreStateCardsScroll,
    showAddresses
  ]);

  const handleAddressClick = async (selected) => {
    activateMap();
    updateMapForLocation(selected.coordinates);
    setSelectedAddress(selected.address);

    const locationId = selected?.id;
    if (!locationId) return;
    const availableLots = getAvailableLotCount(locationId, lotsAvailableByLocationId);

    logEvent('locations_result_clicked', {
      ...locationsAnalyticsBase,
      source: 'locations_map',
      intent: searchIntent || null,
      locationId,
      locationTitle: selected.title || selected.address,
      rankIndex: resultRankByLocationId.get(String(locationId)) ?? null,
      sortMode: effectiveSortMode || 'default',
      activeFilters: activeResultFilters,
      availableLots,
      openForParking: selected.openForParking === true,
    });
  };

  const handleShowAddressesClick = () => {
    const newShowAddresses = !showAddresses;
    if (newShowAddresses) {
      activateMap();
    }
    logEvent('locations_map_toggle_clicked', {
      ...locationsAnalyticsBase,
      source: 'locations_map_toggle',
      intent: searchIntent || null,
      trigger: 'locations_map_button',
      nextState: newShowAddresses ? 'open' : 'closed',
      resultsCount: locationsForResults.length,
    });
    setShowAddresses(newShowAddresses);
    
    if (newShowAddresses) {
      setShouldRestoreStateCardsScroll(false);
      // When showing addresses, scroll to them
      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            if (addressCardsRef.current) {
              scrollElementToTopWithOffset(addressCardsRef.current);
            }
          });
        });
      } else {
        addressCardsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      // When hiding addresses, reset map to initial view and clear selected address
      resetMap();
      setSelectedAddress(null);
      setSelectedLocation(null); // Also clear selectedLocation
      setShouldRestoreStateCardsScroll(true);
    }
  };

  const handleMapPlaceholderCtaClick = useCallback(() => {
    const scopedLocationId = normalizedPricingLocationId || selectedLocation?.id || null;
    logEvent('map_preview_click', {
      source: 'map_placeholder',
      trigger: 'cta_button',
      section: activeSectionKey,
      routePath: location.pathname,
      isPricingRoute,
      locationId: scopedLocationId || undefined,
      hasSelectedLocation: !!selectedLocation,
      mapActivationMode: shouldAutoloadPricingMap ? 'auto' : 'intent',
    });
    logEvent('locations_map_toggle_clicked', {
      ...locationsAnalyticsBase,
      source: 'locations_map_toggle',
      intent: searchIntent || null,
      trigger: 'map_placeholder_cta',
      nextState: 'open',
      resultsCount: locationsForResults.length,
    });
    setShouldRestoreStateCardsScroll(false);
    setShowAddresses(true);
    activateMap();
  }, [
    activateMap,
    activeSectionKey,
    isPricingRoute,
    locationsAnalyticsBase,
    locationsForResults.length,
    location.pathname,
    normalizedPricingLocationId,
    searchIntent,
    selectedLocation,
    shouldAutoloadPricingMap,
  ]);

  const handleBookNow = (price, duration, address = null, source = 'unknown', pricingContext = null) => {
    try {
      if (typeof prewarmHotToast === 'function') {
        prewarmHotToast();
      }

      const resolvedLocation = address
        ? getLocationByAddress(address)
        : getLocationById(selectedLocation?.id);
      const locationId = resolvedLocation?.id;
      const location = resolvedLocation;
      
      if (!location || !locationId) {
        setMessage('Location not found. Please try again.');
        return;
      }

      const availableLots = lotsAvailableByLocationId[locationId];
      const selectedLotData = filterLotsByLocationId(locationId, lotsAvailableByLocationId);
      
      // Pass selectedLotData to validation
      const validationResult = validateBookingRequest(locationId, location, availableLots, selectedLotData);
      
      if (!validationResult.valid) {
        setMessage(validationResult.message);
        return;
      }

      // Set booking details and open modal immediately.
      setBookingDetails(prev => ({
        ...prev,
        address: location.address,
        price,
        duration,
        pricingContext,
        gateCode: location.gateCode || '0000',
        parkingLotId: locationId,
        googlePlaceId: location.googlePlaceId || location.placeId || '',
        isPromoActive: location.isPromoActive || false,
        location: location // Pass full location object for pricing calculations
      }));

      // Set booking source for funnel tracking
      setBookingSource(source);
      
      // Show booking modal
      setShowBookingModal(true);

      // Fetch Google Place ID in background and patch booking details when available.
      fetchGoogleRatingSummary(locationId)
        .then((ratingSummary) => {
          const googlePlaceId = ratingSummary?.placeId || '';
          if (!googlePlaceId) return;

          setBookingDetails(prev => {
            if (prev?.parkingLotId !== locationId) return prev;
            return { ...prev, googlePlaceId };
          });
          console.log('handleBookNow - googlePlaceId fetched from DB:', googlePlaceId);
        })
        .catch((err) => {
          console.warn('Could not fetch Google Place ID from database:', err);
        });
    } catch (error) {
      console.error('Error in handleBookNow:', error);
      setMessage('An error occurred while processing your booking. Please try again.');
    }
  };

  const handleBookNowClick = (address, price, duration, source = 'address_card') => {
    const normalizedSource = source || 'address_card';
    setSelectedAddress(address);
    const locationId = getLocationByAddress(address)?.id;
    logEvent('book_now_click', {
      source: normalizedSource,
      address,
      locationId,
      price,
      duration,
      pricingPlan: duration,
    });
    handleBookNow(price, duration, address, normalizedSource);
  };

  const handlePricingCardBookNowClick = (price, duration, pricingContext = null) => {
    // If coming from locations list, preserve the original source
    const effectiveSource = location.state?.bookingSource || 'pricing_card';
    
    // Track Book Now button click from pricing cards
    logEvent('book_now_click', {
      source: effectiveSource,
      locationId: selectedLocation?.id,
      address: selectedAddress,
      price,
      duration,
      pricingPlan: duration,
    });
    handleBookNow(price, duration, selectedAddress, effectiveSource, pricingContext);
  };

  const handleBookingSubmit = (details) => {
    setBookingDetails(details);
    setShowBookingModal(false);
    setBookingSource(null); // Reset source after successful booking
    setBookingSubmitted(true);

    const submittedLocationId = details?.parkingLotId;
    if (submittedLocationId) {
      Promise.resolve()
        .then(() => retrieveLots({ background: true, locationId: submittedLocationId }))
        .catch((err) => {
          console.warn('Could not refresh lots after booking submission:', err);
        });
    }
  };

  const handleRatingClick = (locationId = null, source = 'state_card') => {
    if (locationId) {
      setRatingLocationId(locationId);
      const matchedLocation = getLocationById(locationId);
      logEvent('location_card_rating_clicked', {
        ...locationsAnalyticsBase,
        source,
        intent: searchIntent || null,
        locationId,
        locationTitle: matchedLocation?.title || matchedLocation?.address,
      });
    }
    setShowReviewModal(true);
  };

  const handleCloseReviewModal = () => {
    setShowReviewModal(false);
  };

  useEffect(() => {
    if (showReviewModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showReviewModal]);

  const deferredMapShell = (
    <div className="w-full max-w-7xl mx-auto px-2 sm:px-3 md:px-4 my-3">
      <div className="relative overflow-hidden rounded-lg bg-[#14324a]/35 h-[300px] md:h-[400px] lg:h-[450px] animate-pulse">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer" />
      </div>
    </div>
  );

  const pricingHydrationShell = (
    <div
      data-testid="pricing-hydration-shell"
      className="w-full max-w-[600px] md:max-w-[980px] lg:max-w-[1080px] mx-auto px-2"
    >
      <div className="h-10 mb-3 rounded-md bg-[#14324a]/20 animate-pulse" />
      <div className="grid grid-cols-2 min-[768px]:grid-cols-4 items-stretch gap-1.5 sm:gap-3 md:gap-4 w-full">
        <div className="h-32 sm:h-36 rounded-lg bg-[#14324a]/20 animate-pulse" />
        <div className="h-32 sm:h-36 rounded-lg bg-[#14324a]/20 animate-pulse" />
        <div className="h-32 sm:h-36 rounded-lg bg-[#14324a]/20 animate-pulse" />
        <div className="h-32 sm:h-36 rounded-lg bg-[#14324a]/20 animate-pulse" />
      </div>
    </div>
  );

  const openStairDelayStepMs = 45;
  const closeStairDelayStepMs = 35;
  const maxStairDelayMs = 360;
  const stairAnimationClasses = `transform-gpu transition-[transform,opacity] duration-300 ease-out ${
    isAmenitiesFiltersOpen ? 'translate-x-0 opacity-100' : 'translate-x-3 opacity-0'
  }`;
  const stairLabelAnimationClasses = `inline-block transform-gpu transition-transform duration-300 ease-out ${
    isAmenitiesFiltersOpen ? 'translate-x-0' : 'translate-x-2'
  }`;
  const getStairDelayStyle = (stairIndex = 0, stairTotal = 1) => {
    const safeTotal = Math.max(stairTotal, 1);
    const bidirectionalIndex = isAmenitiesFiltersOpen
      ? stairIndex
      : Math.max(safeTotal - stairIndex - 1, 0);
    const stairStepMs = isAmenitiesFiltersOpen ? openStairDelayStepMs : closeStairDelayStepMs;

    return {
      transitionDelay: `${Math.min(bidirectionalIndex * stairStepMs, maxStairDelayMs)}ms`,
    };
  };

  const renderQuickFilterChip = (filterOption, stairIndex = 0, stairTotal = 1) => (
    <button
      key={filterOption.key}
      type="button"
      onClick={() => handleQuickFilterToggle(filterOption.key)}
      className={`inline-flex w-fit max-w-full items-center rounded-md px-2 py-1.5 text-left text-sm font-medium whitespace-nowrap transition-colors ${stairAnimationClasses} ${
        quickFilters[filterOption.key]
          ? 'bg-[var(--accent-500)]/12 text-[var(--accent-500)]'
          : 'text-[--lamborghini-white] hover:bg-[--lamborghini-white]/5 hover:text-[var(--accent-500)]'
      }`}
      style={getStairDelayStyle(stairIndex, stairTotal)}
    >
      <span className={`${stairLabelAnimationClasses} block whitespace-nowrap`}>{filterOption.label}</span>
    </button>
  );

  const renderSortChip = (sortOption, stairIndex = 0, stairTotal = 1) => (
    (() => {
      const isNearestSort = sortOption.key === LOCATION_SORT_DISTANCE_ASC;
      const isPriceSort = sortOption.key === LOCATION_SORT_PRICE_ASC;
      const isDisabled = isNearestSort && isGeoLocating;
      const label = isNearestSort && isGeoLocating ? 'Locating...' : sortOption.label;

      return (
    <button
      key={sortOption.key}
      type="button"
      disabled={isDisabled}
      onClick={() => handleSortModeChange(
        effectiveSortMode === sortOption.key ? null : sortOption.key,
      )}
      className={`inline-flex w-fit max-w-full items-center rounded-md px-2 py-1.5 text-left text-sm font-medium whitespace-nowrap transition-colors ${stairAnimationClasses} ${
        effectiveSortMode === sortOption.key
          ? 'bg-[var(--accent-500)]/12 text-[var(--accent-500)]'
          : 'text-[--lamborghini-white] hover:bg-[--lamborghini-white]/5 hover:text-[var(--accent-500)]'
      } ${isDisabled ? 'cursor-not-allowed opacity-70' : ''}`}
      aria-pressed={effectiveSortMode === sortOption.key}
      style={getStairDelayStyle(stairIndex, stairTotal)}
    >
      <span className="inline-flex items-center gap-1 whitespace-nowrap">
        {isPriceSort && <CurrencyDollarIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
        <span className={`${stairLabelAnimationClasses} whitespace-nowrap`}>{label}</span>
      </span>
    </button>
      );
    })()
  );

  const shouldShowScopeFilters = shouldShowJourneyFilters && !isPricingRoute && !selectedLocation;

  const scopeFilterControls = shouldShowScopeFilters ? (
    <div className="max-w-7xl mx-auto px-2 sm:px-3 md:px-4 mb-1">
      <SearchBar />
    </div>
  ) : null;

  const amenitiesToggleButtonClasses = `inline-flex shrink-0 items-center gap-1 px-1 py-1 text-sm font-medium whitespace-nowrap transition-colors ${
    isAmenitiesFiltersOpen
      ? 'text-[var(--accent-500)]'
      : 'text-[--lamborghini-white] hover:text-[var(--accent-500)]'
  }`;

  const shouldShowLocationsCountPill = !isLocationsRoute;
  const locationsCountPill = shouldShowLocationsCountPill ? (
    <span className="inline-flex shrink-0 items-center justify-start self-start px-1 py-1 text-left text-sm font-medium whitespace-nowrap text-[--lamborghini-white]">
      {locationsForResults.length} {locationsForResults.length === 1 ? 'location' : 'locations'}
    </span>
  ) : null;
  const shouldShowAmenitiesFilterUi = shouldShowJourneyFilters && locationsForResults.length > 1;
  const sortFilterQuickPicks = useMemo(
    () => (shouldShowStateCardsPanel
      ? LOCATION_SORT_FILTER_QUICK_PICKS.filter((option) => option.key === 'comingSoon')
      : LOCATION_SORT_FILTER_QUICK_PICKS),
    [shouldShowStateCardsPanel],
  );
  const visibleRefinementCount = promotedRefinementFilters.length + (isRefinementPanelOpen ? hiddenRefinementFilters.length : 0);
  const hasMoreFiltersToggle = hiddenRefinementFilters.length > 0;
  const sortStairOffset = visibleRefinementCount + (hasMoreFiltersToggle ? 1 : 0);
  const filterActionChipCount = sortStairOffset
    + LOCATION_SORT_QUICK_PICKS.length
    + sortFilterQuickPicks.length;

  const filterActionChips = (
    <>
      <div className="flex flex-col items-start gap-1">
        {promotedRefinementFilters.map((filterOption, index) => renderQuickFilterChip(filterOption, index, filterActionChipCount))}
        {isRefinementPanelOpen && hiddenRefinementFilters.map((filterOption, index) => (
          renderQuickFilterChip(filterOption, promotedRefinementFilters.length + index, filterActionChipCount)
        ))}
        {hasMoreFiltersToggle && (
          <button
            type="button"
            onClick={() => setIsRefinementPanelOpen((previous) => !previous)}
            className={`inline-flex w-fit max-w-full items-center rounded-md px-2 py-1.5 text-left text-sm font-medium whitespace-nowrap text-[--lamborghini-blue] transition-colors hover:bg-[--lamborghini-white]/5 hover:text-[var(--accent-500)] ${stairAnimationClasses}`}
            style={getStairDelayStyle(visibleRefinementCount, filterActionChipCount)}
          >
            <span className={`${stairLabelAnimationClasses} block whitespace-nowrap`}>
              {isRefinementPanelOpen
                ? 'Less filters'
                : `More filters${activeRefinementCount > 0 ? ` (${activeRefinementCount})` : ''}`}
            </span>
          </button>
        )}
      </div>

      <div className="mt-1 pt-1">
        <p className="mb-1 px-1 text-xs font-semibold uppercase tracking-wide text-[--lamborghini-white]">
          Sort results
        </p>
        <div className="flex flex-col gap-1">
          {LOCATION_SORT_QUICK_PICKS.map((sortOption, index) => renderSortChip(
            sortOption,
            sortStairOffset + index,
            filterActionChipCount,
          ))}
          {sortFilterQuickPicks.map((filterOption, index) => renderQuickFilterChip(
            filterOption,
            sortStairOffset + LOCATION_SORT_QUICK_PICKS.length + index,
            filterActionChipCount,
          ))}
        </div>
      </div>
    </>
  );

  const shouldShowInlineSortControls = shouldShowJourneyFilters
    && !selectedLocation
    && !showAddresses
    && locationsForResults.length > 1;

  const inlineSortControls = shouldShowInlineSortControls ? (
    <div className="flex min-w-0 flex-col items-start gap-1" data-testid="inline-sort-controls">
      <div className="flex flex-wrap items-center gap-1">
        {LOCATION_SORT_QUICK_PICKS.map((sortOption) => {
          const isNearestSort = sortOption.key === LOCATION_SORT_DISTANCE_ASC;
          const isDisabled = isNearestSort && isGeoLocating;
          const isActive = effectiveSortMode === sortOption.key;
          const label = isNearestSort && isGeoLocating ? 'Locating...' : sortOption.label;

          return (
            <button
              key={`mobile-sort-${sortOption.key}`}
              type="button"
              disabled={isDisabled}
              onClick={() => handleSortModeChange(isActive ? null : sortOption.key)}
              aria-pressed={isActive}
              className={`inline-flex items-center rounded-md px-2 py-1 text-xs sm:px-2.5 sm:py-1.5 sm:text-sm font-medium whitespace-nowrap transition-colors ${
                isActive
                  ? 'bg-[var(--accent-500)]/12 text-[var(--accent-500)]'
                  : 'text-[--lamborghini-white] hover:bg-[--lamborghini-white]/5 hover:text-[var(--accent-500)]'
              } ${isDisabled ? 'cursor-not-allowed opacity-70' : ''}`}
            >
              {label}
            </button>
          );
        })}
      </div>
      {geolocationError && (
        <div className="px-1 text-[11px] text-[--lamborghini-orange]" role="status" aria-live="polite">
          Turn on location to sort by nearest.
        </div>
      )}
    </div>
  ) : null;

  const stateCardsHeaderControls = isMobileFilterViewport ? inlineSortControls : null;
  const locationsResultsScrollShellClasses = 'state-cards-scroll state-cards-scroll-edge state-cards-large-viewport-scroll rounded-md rounded-r-none';

  const useAmenitiesSidebarLayout = !selectedLocation
    && !showAddresses
    && shouldShowAmenitiesFilterUi
    && !isMobileFilterViewport;
  const amenitiesFiltersPanelId = useAmenitiesSidebarLayout
    ? 'locations-filters-sidebar-panel'
    : 'locations-filters-inline-panel';

  const amenitiesSidebarPanelClasses = `relative w-full transition-[transform,opacity,height] duration-300 ease-out will-change-transform ${
    isAmenitiesFiltersOpen
      ? 'h-auto translate-x-0 opacity-100 pointer-events-auto'
      : 'h-0 translate-x-full opacity-0 pointer-events-none overflow-hidden'
  }`;

  useEffect(() => {
    if (useAmenitiesSidebarLayout) {
      setIsAmenitiesFiltersOpen(false);
    }
  }, [useAmenitiesSidebarLayout]);

  const shouldShowSingleResultClearControl =
    shouldShowJourneyFilters && !isMobileFilterViewport && !shouldShowAmenitiesFilterUi && hasNonDefaultFilters;

  const amenitiesFilterControls = shouldShowAmenitiesFilterUi && !isMobileFilterViewport ? (
    <div className={`overflow-hidden px-2 sm:px-3 ${useAmenitiesSidebarLayout ? 'pt-1 pb-0' : 'pt-2 pb-1'}`}>
      <div className="mb-0 flex items-center justify-between gap-2 px-1 pb-0">
        <button
          type="button"
          onClick={() => setIsAmenitiesFiltersOpen((previous) => !previous)}
          aria-expanded={isAmenitiesFiltersOpen}
          aria-controls={amenitiesFiltersPanelId}
          className={amenitiesToggleButtonClasses}
        >
          {useAmenitiesSidebarLayout ? (
            isAmenitiesFiltersOpen ? (
              <ArrowRightIcon className="h-3.5 w-3.5" />
            ) : (
              <ArrowLeftIcon className="h-3.5 w-3.5" />
            )
          ) : (
            isAmenitiesFiltersOpen ? <ChevronUpIcon className="h-3.5 w-3.5" /> : <ChevronDownIcon className="h-3.5 w-3.5" />
          )}
          {isAmenitiesFiltersOpen ? 'Hide filters' : 'Show filters'}
        </button>
        {inlineSortControls && (
          <div className="min-w-0 flex-1 flex justify-center">
            {inlineSortControls}
          </div>
        )}
        {locationsCountPill}
      </div>
      {!useAmenitiesSidebarLayout && (
        <div
          id={amenitiesFiltersPanelId}
          aria-hidden={!isAmenitiesFiltersOpen}
          className={`overflow-hidden transition-[max-height,opacity,transform] duration-300 ease-out ${
            isAmenitiesFiltersOpen ? 'max-h-[32rem] opacity-100 translate-x-0 mt-1' : 'max-h-0 opacity-0 translate-x-2'
          }`}
        >
          <div className="px-2">
            {filterActionChips}
            {hasNonDefaultFilters && (
              <button
                type="button"
                onClick={handleClearQuickFilters}
                className="mt-1 inline-flex w-fit items-center rounded-md px-2 py-1.5 text-left text-sm font-medium whitespace-nowrap text-[--lamborghini-white] transition-colors hover:bg-[--lamborghini-white]/5 hover:text-[var(--accent-500)]"
              >
                Clear filters
              </button>
            )}
            {geolocationError && (
              <div className="mt-1 text-[11px] text-[--lamborghini-orange]" role="status" aria-live="polite">
                Turn on location to sort by nearest.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  ) : shouldShowSingleResultClearControl ? (
    <div className="overflow-hidden px-2 sm:px-3 pt-1 pb-0">
      <div className="mb-0 flex items-center justify-between gap-2 px-1 pb-0">
        <button
          type="button"
          onClick={handleClearQuickFilters}
          className="inline-flex items-center gap-1 px-1 py-1 text-sm font-medium whitespace-nowrap text-[--lamborghini-white] transition-colors hover:text-[var(--accent-500)]"
        >
          Clear filters
        </button>
        {locationsCountPill}
      </div>
    </div>
  ) : null;

  const amenitiesFilterSidebar = useAmenitiesSidebarLayout ? (
    <div className="h-auto w-max pt-0.5 pl-1 pr-0.5 pb-1">
      <div className="relative h-auto w-max overflow-visible">
        <div
          id={amenitiesFiltersPanelId}
          aria-hidden={!isAmenitiesFiltersOpen}
          className={amenitiesSidebarPanelClasses}
        >
          <div className="flex flex-col items-start">
            <div className="overflow-x-hidden pr-0.5">
              <div className="space-y-1">
                {filterActionChips}
                {geolocationError && (
                  <div className="px-1 text-[11px] text-[--lamborghini-orange]" role="status" aria-live="polite">
                    Turn on location to sort by nearest.
                  </div>
                )}
              </div>
            </div>
            {hasNonDefaultFilters && (
              <div className="mt-1 border-t border-[--lamborghini-white]/15 pt-1 pr-0.5">
                <button
                  type="button"
                  onClick={handleClearQuickFilters}
                  className="inline-flex w-fit items-center rounded-md px-2 py-1.5 text-left text-sm font-medium whitespace-nowrap text-[--lamborghini-white] transition-colors hover:bg-[--lamborghini-white]/5 hover:text-[var(--accent-500)]"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  ) : null;

  const locationsRouteDesktopColumnsClass = useAmenitiesSidebarLayout
    ? (isAmenitiesFiltersOpen ? 'grid-cols-[max-content_minmax(0,1fr)]' : 'grid-cols-[0rem_minmax(0,1fr)]')
    : 'grid-cols-1';
  const locationsRouteLeftRailClasses = isAmenitiesFiltersOpen
    ? 'w-max self-start py-0.5 pr-0 rounded-md overflow-visible opacity-100'
    : 'w-0 self-stretch py-0 pr-0 rounded-md overflow-hidden opacity-0 pointer-events-none';

  useEffect(() => {
    if (!shouldShowAmenitiesFilterUi && isAmenitiesFiltersOpen) {
      setIsAmenitiesFiltersOpen(false);
    }
  }, [isAmenitiesFiltersOpen, shouldShowAmenitiesFilterUi]);

  const selectedLocationCanonicalPath = useMemo(() => {
    if (!selectedLocation) return null;
    return buildCanonicalPricingPath(selectedLocation) || null;
  }, [selectedLocation]);

  const handleSeoBookLocationNowClick = useCallback(() => {
    if (pricingSectionRef.current) {
      scrollElementToTopWithOffset(pricingSectionRef.current);
      return;
    }

    if (
      selectedLocationCanonicalPath
      && location.pathname !== selectedLocationCanonicalPath
    ) {
      navigate(selectedLocationCanonicalPath);
    }
  }, [
    location.pathname,
    navigate,
    scrollElementToTopWithOffset,
    selectedLocationCanonicalPath,
  ]);

  const seoProps = useMemo(
    () => generateSEOProps({
      pathname: location.pathname,
      routeSeoMeta: getRouteSeoMeta(location.pathname),
      preselectedLocationId: isPricingRoute ? (normalizedPricingLocationId || selectedLocation?.id) : null,
      selectedAddress,
      selectedLocation: isPricingRoute ? selectedLocation : null,
      currentSEO,
      sectionKey: activeSectionKey,
      keywords: keywords.join(', '),
    }),
    [
      activeSectionKey,
      currentSEO,
      isPricingRoute,
      location.pathname,
      normalizedPricingLocationId,
      selectedAddress,
      selectedLocation,
    ],
  );

  const pricingSeoSchemas = useMemo(
    () => {
      if (!isPricingRoute) return [];

      const locationSchemas = buildLocationPricingSchemas({
        location: selectedLocation,
        canonicalUrl: seoProps.url,
      });

      if (locationSchemas.length > 0) {
        return locationSchemas;
      }

      return [
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Truck parking pricing',
          url: seoProps.url,
          description: seoProps.description,
        },
      ];
    },
    [isPricingRoute, seoProps.description, seoProps.url, selectedLocation],
  );

  const truckParkingDiscoveryDataset = useMemo(
    () => buildTruckParkingDiscoveryDataset(addresses),
    [addresses],
  );
  const homepageDiscoveryContent = useMemo(
    () => buildTruckParkingHomepageContent({ dataset: truckParkingDiscoveryDataset }),
    [truckParkingDiscoveryDataset],
  );
  const truckParkingRouteContent = useMemo(
    () => buildTruckParkingRouteContent({
      dataset: truckParkingDiscoveryDataset,
      routeMeta: truckParkingRouteMeta,
    }),
    [truckParkingDiscoveryDataset, truckParkingRouteMeta],
  );
  const includeDefaultSeoSchemas = !isPricingRoute && activeSectionKey !== 'pm_partners';

  const shouldShowCompactPageHeading = isLocationsRoute;
  const compactPageHeadingText = useMemo(
    () => buildCompactLocationsHeading({
      routeMeta: truckParkingRouteMeta,
      fallbackTitle: seoProps.title,
    }),
    [seoProps.title, truckParkingRouteMeta],
  );

  return (
    <div className={`home-container ${shouldShowSectionAtmosphere ? 'home-container--network-atmosphere' : ''}`}>
      {shouldShowSectionAtmosphere && <div className="home-container__network-atmosphere" aria-hidden="true" />}
      <Suspense fallback={
        <>
          <CardSkeleton />
          
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 lg:gap-4 max-w-7xl mx-auto px-2 sm:px-3 md:px-4">
            {Array(6).fill().map((_, index) => (
              <StateCardSkeleton key={`skeleton-${index}`} />
            ))}
          </div>
          
          {deferredMapShell}
        </>
      }>
        <SEO
          {...seoProps}
          schemas={pricingSeoSchemas}
          includeDefaultSchemas={includeDefaultSeoSchemas}
        />
        {shouldShowCompactPageHeading && !shouldShowNetworkRebuildFlow && (
          <section className="mx-auto mb-1 mt-1 w-full max-w-7xl px-2 sm:px-3 md:px-4" aria-label="Page heading">
            <h1 className="overflow-hidden text-ellipsis text-[13px] font-semibold leading-tight text-[--lamborghini-white] sm:text-sm md:text-base [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]" title={compactPageHeadingText}>
              {compactPageHeadingText}
            </h1>
          </section>
        )}
        {bookingSubmitted ? (
          <BookingConfirmation />
        ) : isInitialLotsLoading || isTransitioning ? (
          // Loading state with all skeleton components
          <>
            <CardSkeleton />
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 lg:gap-4 max-w-7xl mx-auto px-2 sm:px-3 md:px-4">
              {Array(6).fill().map((_, index) => (
                <StateCardSkeleton key={`skeleton-${index}`} />
              ))}
            </div>
            
            {deferredMapShell}
          </>
        ) : shouldShowNetworkRebuildFlow ? (
          <ListYourLotContent initialPlanId="marketplace-driver" />
        ) : (
          <>
            {!selectedLocation && isLocationsRoute && scopeFilterControls}
            {!shouldHideLocationsJourneyPanel && (
              <Card
                showRating={!!selectedLocation?.id}
                hideHolidayBanner={!!selectedLocation?.id || isCompactHomeAdSlider}
                hideSocialLinks={isCompactHomeAdSlider}
                hideThemeToggle={isCompactHomeAdSlider}
                frameless={isCompactHomeAdSlider}
                className={
                  isLocationsRoute && !selectedLocation
                    ? 'px-1 sm:px-2 md:px-3 p-1.5 sm:p-2 !bg-transparent dark:!bg-transparent !shadow-none dark:!shadow-none'
                    : (activeSectionKey === 'rates' || activeSectionKey === 'about') && !selectedLocation
                      ? 'px-1 sm:px-2 md:px-3 p-1.5 sm:p-2 !bg-transparent dark:!bg-transparent !shadow-none dark:!shadow-none'
                      : ''
                }
              >
                <div className={`relative w-full ${shouldShowHomeMotion ? 'home-motion-shell' : ''}`}>
                  {shouldShowHomeMotion && (
                    <div className="home-motion-atmosphere" aria-hidden="true">
                      <div className="home-motion-atmosphere__glow" />
                      <div className="home-motion-atmosphere__yard" />
                      <div className="home-motion-atmosphere__route home-motion-atmosphere__route--one">
                        <span className="home-motion-atmosphere__vehicle" />
                      </div>
                      <div className="home-motion-atmosphere__route home-motion-atmosphere__route--two">
                        <span className="home-motion-atmosphere__vehicle" />
                      </div>
                      <div className="home-motion-atmosphere__ticker">
                        <span className="home-motion-atmosphere__signal" />
                        <span>LIVE YARD NETWORK</span>
                        <span className="home-motion-atmosphere__ticker-divider">/</span>
                        <span>SECURE PARKING</span>
                        <span className="home-motion-atmosphere__ticker-divider">/</span>
                        <span>BOOK IN MINUTES</span>
                      </div>
                    </div>
                  )}
                  {/* Only show rating on home/AdSlider view, hide on locations/rates/about */}
                  {activeSectionKey === 'home' && !!selectedLocation?.id && !isCompactHomeAdSlider && (
                    <div className="absolute left-2 top-2 z-20 sm:left-3 sm:top-3">
                      <GoogleRatingDisplay 
                        onClick={() => handleRatingClick(selectedLocation.id, 'pricing_header_rating')} 
                        locationId={selectedLocation.id}
                        locationAddress={selectedLocation.address} 
                      />
                    </div>
                  )}
                  {activeSectionKey === 'home' ? (
                    <div
                      key="home-content"
                      className={shouldShowYardV2Hero ? 'grid w-full items-center gap-4 px-[18px] lg:grid-cols-[minmax(0,0.9fr)_minmax(440px,1.1fr)] lg:gap-6 xl:grid-cols-[minmax(0,0.92fr)_minmax(520px,1.08fr)] xl:px-10' : undefined}
                    >
                      {shouldShowYardV2Hero && (
                        <section className="pt-2 text-left md:pt-0 xl:pt-0" aria-label="Truck parking booking overview">
                          <div className="mb-1 inline-flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-[#5bd869] sm:text-[11px] md:mb-3">
                            <span
                              aria-hidden="true"
                              className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#5bd869] shadow-[0_0_12px_rgba(91,216,105,0.72)] animate-pulse motion-reduce:animate-none"
                            />
                            <span>{heroOpenSpotsTonight > 0 ? `${heroOpenSpotsTonight} spots available now` : 'Spots available now'}</span>
                            <span className="text-[#F3EEDF]/45">· updated live</span>
                          </div>
                          <h1 className="hidden max-w-[13ch] font-grotesk text-[36px] font-bold leading-none tracking-[-0.03em] text-[#F3EEDF] md:block md:text-[44px] lg:text-[52px] xl:text-[56px]">
                            Park your truck by the{' '}
                            <span className="text-[#FFC846]">night, week,</span>
                            <br />
                            or <span className="text-[#FFC846]">month.</span>
                          </h1>
                          <p className="mt-4 hidden max-w-[42rem] text-[13px] font-medium leading-relaxed text-[#F3EEDF]/55 md:block md:text-sm xl:max-w-[28rem]">
                            {heroLocationCount} parking {heroLocationCount === 1 ? 'location' : 'locations'} across NC, SC, GA, OH, and PA. Book nightly, weekly, or monthly parking with no long-term contract.
                          </p>
                        </section>
                      )}
                      <AdSlider
                        items={adItems}
                        intervalMs={5000}
                        locationAddress={selectedLocation?.address}
                        compact={isCompactHomeAdSlider}
                        onCtaClick={(item) => {
                          const targetId = item.locationId;
                          if (!targetId) {
                            console.log('[AdSlider][CTA] Clicked without locationId', { item });
                            return;
                          }
                          logAdEvent('ad_click', { adId: item.id, locationId: targetId, isPromo: !!item.isPromo });
                          const matchedLocation = getLocationById(targetId);
                          console.log('[AdSlider][CTA] Click', { item, targetId, matchedLocationFound: !!matchedLocation });
                          if (matchedLocation) {
                            setSelectedLocation(matchedLocation);
                            setSelectedAddress(matchedLocation.address);
                            setGateCode(matchedLocation.gateCode || '0000');
                            updateMapForLocation(matchedLocation.coordinates);
                            setTitle(matchedLocation.address);
                          }
                          const pricingPath = getPricingPathForLocation(matchedLocation || targetId) || `/pricing/${targetId}`;
                          navigate(pricingPath, { state: { bookingSource: 'ad_slider' } });
                        }}
                      />
                    </div>
                  ) : (
                    <div key={activeSectionKey} className="flex flex-col items-center w-full">
                      {shouldShowLocationsFiltersInTopCard ? (
                        <div className="w-full mt-1">
                          {amenitiesFilterControls}
                          {useAmenitiesSidebarLayout ? (
                            <div className={`grid items-stretch gap-x-0 transition-[grid-template-columns] duration-300 ease-out ${locationsRouteDesktopColumnsClass}`}>
                              <div className={locationsRouteLeftRailClasses}>
                                {amenitiesFilterSidebar}
                              </div>
                              <div className="min-w-0">
                                {isLocationsRoute ? (
                                  <div className={locationsResultsScrollShellClasses}>
                                    <SelectedContent
                                      locations={locationsForResults}
                                      mobileHeaderControls={stateCardsHeaderControls}
                                      showNearestDistanceContext={effectiveSortMode === LOCATION_SORT_DISTANCE_ASC}
                                      distanceMilesByLocationId={distanceMilesByLocationId}
                                    />
                                  </div>
                                ) : (
                                  <SelectedContent
                                    locations={locationsForResults}
                                    mobileHeaderControls={stateCardsHeaderControls}
                                    showNearestDistanceContext={effectiveSortMode === LOCATION_SORT_DISTANCE_ASC}
                                    distanceMilesByLocationId={distanceMilesByLocationId}
                                  />
                                )}
                              </div>
                            </div>
                          ) : (
                            isLocationsRoute ? (
                              <div className={locationsResultsScrollShellClasses}>
                                <SelectedContent
                                  locations={locationsForResults}
                                  mobileHeaderControls={stateCardsHeaderControls}
                                  showNearestDistanceContext={effectiveSortMode === LOCATION_SORT_DISTANCE_ASC}
                                  distanceMilesByLocationId={distanceMilesByLocationId}
                                />
                              </div>
                            ) : (
                              <SelectedContent
                                locations={locationsForResults}
                                mobileHeaderControls={stateCardsHeaderControls}
                                showNearestDistanceContext={effectiveSortMode === LOCATION_SORT_DISTANCE_ASC}
                                distanceMilesByLocationId={distanceMilesByLocationId}
                              />
                            )
                          )}
                        </div>
                      ) : (
                        isLocationsRoute ? (
                          <div className={locationsResultsScrollShellClasses}>
                            <SelectedContent
                              locations={locationsForResults}
                              mobileHeaderControls={stateCardsHeaderControls}
                              showNearestDistanceContext={effectiveSortMode === LOCATION_SORT_DISTANCE_ASC}
                              distanceMilesByLocationId={distanceMilesByLocationId}
                            />
                          </div>
                        ) : (
                          <SelectedContent
                            locations={locationsForResults}
                            mobileHeaderControls={stateCardsHeaderControls}
                            showNearestDistanceContext={effectiveSortMode === LOCATION_SORT_DISTANCE_ASC}
                            distanceMilesByLocationId={distanceMilesByLocationId}
                          />
                        )
                      )}
                    </div>
                  )}
                </div>
              </Card>
            )}
            {!selectedLocation && !isLocationsRoute && scopeFilterControls}

            {isPricingHydrating ? (
              <>
                {pricingHydrationShell}
              </>
            ) : !selectedLocation ? (
              <>
                {historicalPricingLocation && (
                  <div className="max-w-7xl mx-auto px-2 sm:px-3 md:px-4">
                    <div className="location-unavailable-card" role="status" aria-live="polite">
                      <div className="location-unavailable-card__content">
                        <div className="location-unavailable-card__eyebrow">
                          <span className="location-unavailable-card__pulse" aria-hidden="true" />
                          Older Google listing
                        </div>
                        <h2>Parking at this address is managed by {retiredLocationTenantName}.</h2>
                        <p>
                          You reached Truck Parking Hub from an older Google result. To reserve a space here, continue to the operator’s booking website.
                        </p>
                        <div className="location-unavailable-card__actions">
                          <a
                            className="location-unavailable-card__primary"
                            href={retiredLocationDestination}
                            onClick={() => logEvent('tenant_handoff', {
                              source: 'retired_location_notice',
                              locationId: normalizedPricingLocationId,
                              destination: retiredLocationDestination,
                            })}
                          >
                            <span>
                              <strong>Reserve parking with {retiredLocationTenantName}</strong>
                              <small>{retiredLocationDestination.replace(/^https?:\/\//, '').replace(/\/$/, '')}</small>
                            </span>
                            <ArrowRightIcon aria-hidden="true" />
                          </a>
                          <button
                            type="button"
                            className="location-unavailable-card__secondary"
                            onClick={handleBrowseCurrentLocations}
                          >
                            Find parking with Truck Parking Hub instead
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* StateCards collapse while map locations view is open */}
                {shouldShowStateCardsPanel && (!isPricingLocationNotFound || (!isRetiredLocationHandoffLoading && !historicalPricingLocation)) && (
                  <div
                    key="state-cards-panel"
                    ref={stateCardsSectionRef}
                    data-testid="state-cards-panel-shell"
                    className="w-full overflow-hidden"
                    style={{ overflowAnchor: 'none' }}
                  >
                    <div className="max-w-7xl mx-auto px-2 sm:px-3 md:px-4">
                      {locationsForResults.length === 0 ? (
                        <NoResultsCard onClearAllFilters={handleClearNoResultsFilters} />
                      ) : isInitialLotsLoading ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 lg:gap-4">
                          {Array(6).fill().map((_, index) => (
                            <StateCardSkeleton key={`skeleton-${index}`} />
                          ))}
                        </div>
                      ) : (
                        <StateCardList
                          locations={locationsForResults}
                          lotsAvailableByLocationId={lotsAvailableByLocationId}
                          amenitiesFromConfigById={amenitiesFromConfigById}
                          defaultRates={defaultRates}
                          nextAvailableDates={nextAvailableDates}
                          distanceMilesByLocationId={distanceMilesByLocationId}
                          showNearestDistanceContext={effectiveSortMode === LOCATION_SORT_DISTANCE_ASC}
                          onCardClick={handleStateCardClick}
                          onRatingClick={handleRatingClick}
                          checkoutStatus={checkoutStatus}
                          topContent={amenitiesFilterControls}
                          mobileHeaderControls={stateCardsHeaderControls}
                          leftColumnContent={amenitiesFilterSidebar}
                          leftColumnCollapsed={useAmenitiesSidebarLayout && !isAmenitiesFiltersOpen}
                        />
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="w-full max-w-[600px] md:max-w-[980px] lg:max-w-[1080px] mx-auto px-2">
                  {(() => {
                    const amenities = amenitiesFromConfigById[selectedLocation.id] || {};
                    const anyFlag = !!(amenities.fenced || amenities.paved || amenities.gravel || amenities.lights || amenities.access24h || amenities.nearGas || amenities.nearHotels || amenities.nearFood || amenities.serviceAvailable);
                    const anyList = (Array.isArray(amenities.nearbyGasStations) && amenities.nearbyGasStations.length > 0)
                      || (Array.isArray(amenities.nearbyHotels) && amenities.nearbyHotels.length > 0)
                      || (Array.isArray(amenities.nearbyFood) && amenities.nearbyFood.length > 0)
                      || (Array.isArray(amenities.services) && amenities.services.length > 0);
                    const hasAmenities = anyFlag || anyList;
                    const tabButtonClass = 'inline-flex h-7 sm:h-8 items-center justify-center rounded-full px-2.5 sm:px-3 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.14em] transition-all duration-200';
                    const isRatesPanelActive = activePricingPanel === 'rates';
                    const isAmenitiesPanelActive = activePricingPanel === 'amenities';
                    return (
                      <>
                        <div className="mb-2 flex items-center justify-center gap-2 sm:gap-2.5" role="tablist" aria-label="Location pricing view">
                          <button
                            type="button"
                            onClick={() => showPricingPanel('rates')}
                            role="tab"
                            aria-selected={isRatesPanelActive}
                            className={`${tabButtonClass} ${isRatesPanelActive ? 'px-3 sm:px-3.5 tracking-[0.16em] bg-[#F3BB36] text-[#0A0A0A] shadow-[0_8px_24px_rgba(243,187,54,0.22)]' : 'text-[#B8C1D6] hover:text-[#E6ECF8] hover:bg-white/[0.04]'}`}
                          >
                            Rates
                          </button>
                          <button
                            type="button"
                            disabled={!hasAmenities}
                            onClick={() => {
                              if (!hasAmenities) return;
                              logEvent('location_card_amenities_opened', {
                                ...locationsAnalyticsBase,
                                source: 'pricing_tab',
                                intent: searchIntent || null,
                                locationId: selectedLocation.id,
                                locationTitle: selectedLocation.title || selectedLocation.address,
                              });
                              showPricingPanel('amenities');
                            }}
                            role="tab"
                            aria-selected={isAmenitiesPanelActive}
                            className={`${tabButtonClass} ${isAmenitiesPanelActive ? 'px-3 sm:px-3.5 tracking-[0.16em] bg-[#58aaf8] text-[#06101f] shadow-[0_8px_24px_rgba(88,170,248,0.2)]' : 'text-[#B8C1D6] hover:text-[#E6ECF8] hover:bg-white/[0.04]'} disabled:cursor-not-allowed disabled:opacity-40`}
                          >
                            Amenities
                          </button>
                          <button
                            type="button"
                            onClick={handleBackToStatesClick}
                            className={`${tabButtonClass} text-[#B8C1D6] hover:text-[#E6ECF8] hover:bg-white/[0.04]`}
                            aria-label="Back to truck parking locations"
                          >
                            Locations
                          </button>
                        </div>
                      </>
                    );
                  })()}
                  
                  <div
                    className={`
                      flex items-center justify-center overflow-hidden mb-2 transition-[height,opacity] duration-200
                      ${isRefreshingLots ? 'h-6 opacity-100' : 'h-0 opacity-0'}
                    `}
                    aria-live="polite"
                  >
                    {isRefreshingLots && (
                      <span
                        data-testid="lots-refresh-badge"
                        className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium bg-[var(--accent-500)]/15 text-[var(--accent-500)] border border-[var(--accent-500)]/35"
                      >
                        Refreshing availability...
                      </span>
                    )}
                  </div>

                  <div ref={pricingSectionRef} className="w-full">
                    {(() => {
                      // Get location-specific rates or use defaults
                      const locationRates = selectedLocation ? getLocationRates(selectedLocation) : { daily: defaultRates.dailyRate, weekly: defaultRates.weeklyRate };
                      
                      // Apply promo discount if promo is active
                      const promoDiscount = selectedLocation ? getLocationPromoDiscount(selectedLocation) : 0.15;
                      const dailyPrice = selectedLocation?.isPromoActive 
                        ? calculatePromoPrice(locationRates.daily, promoDiscount)
                        : locationRates.daily;
                      const weeklyPrice = selectedLocation?.isPromoActive 
                        ? calculatePromoPrice(locationRates.weekly, promoDiscount)
                        : locationRates.weekly;
                      const monthlyDisplay = getLocationMonthlyDisplayMeta(selectedLocation);
                      const monthlyPrice = monthlyDisplay.amount;
                      const noContractPrice = getLocationNoContractRate(
                        selectedLocation,
                        defaultRates.noContractRate
                      );
                      
                      const promoLabel = getPromoDiscountLabel(selectedLocation);
                      
                      const locationPricingOptions = [
                        { price: String(dailyPrice), duration: "per day" },
                        { price: String(weeklyPrice), duration: "per week" },
                        { price: String(noContractPrice), duration: "no contract" },
                        { price: String(monthlyPrice), duration: "per month" },
                      ];

                      const amenityGroups = pricingAmenityGroups;
                      const amenityPages = chunkAmenityGroups(amenityGroups, 2);
                      const activeAmenityPage = amenityPages[activeAmenitySlide] || amenityPages[0] || [];
                      return (
                        <section
                          className="relative overflow-visible touch-pan-y transition-[height] duration-300 ease-out"
                          aria-label={activePricingPanel === 'amenities' ? 'Location amenities' : 'Location rates'}
                          style={pricingPanelHeight > 0 ? { height: `${pricingPanelHeight}px` } : undefined}
                          onTouchStart={handlePricingPanelTouchStart}
                          onTouchEnd={handlePricingPanelTouchEnd}
                        >
                          <div className="overflow-x-hidden">
                            <div
                              className="flex w-[200%] transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform"
                              style={{ transform: activePricingPanel === 'amenities' ? 'translateX(-50%)' : 'translateX(0%)' }}
                            >
                              <div ref={pricingRatesPanelRef} className="w-1/2 shrink-0 pr-1 pb-4 sm:pr-1.5 sm:pb-4">
                                <div className="grid grid-cols-2 min-[768px]:grid-cols-4 items-stretch gap-1.5 sm:gap-3 md:gap-4 w-full">
                                  {locationPricingOptions.map((option, index) => (
                                    <div
                                      key={index}
                                      className="col-span-1"
                                    >
                                      <PricingCard
                                        price={option.price}
                                        duration={option.duration}
                                        savings={getPricingCardSavings({
                                          duration: option.duration,
                                          isPromoActive: selectedLocation?.isPromoActive,
                                          baseDailyRate: locationRates.daily,
                                          baseWeeklyRate: locationRates.weekly,
                                          displayedDailyRate: dailyPrice,
                                          displayedWeeklyRate: weeklyPrice,
                                          monthlyRate: monthlyPrice,
                                          noContractRate: noContractPrice,
                                        })}
                                        onBook={() => handlePricingCardBookNowClick(option.price, option.duration, {
                                          displayedPrice: Number(option.price),
                                          monthlyDisplayMode: option.duration === 'per month' || option.duration === 'no contract'
                                            ? monthlyDisplay.mode
                                            : null,
                                        })}
                                        disabled={false}
                                        isPromoActive={selectedLocation.isPromoActive}
                                        promoDiscountLabel={promoLabel}
                                        monthlyDisplayMode={monthlyDisplay.mode}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>

                              <div ref={pricingAmenitiesPanelRef} className="w-1/2 shrink-0 pl-1 pb-4 sm:pl-1.5 sm:pb-4">
                                {activeAmenityPage.length > 0 && (
                                  <div className="space-y-2.5">
                                    <div className="overflow-hidden rounded-[16px]">
                                      <div
                                        className="flex transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform"
                                        style={{ transform: `translateX(-${activeAmenitySlide * 100}%)` }}
                                      >
                                        {amenityPages.map((pageGroups, pageIndex) => {
                                          const isActiveAmenitySlide = pageIndex === activeAmenitySlide;
                                          return (
                                            <div
                                              key={`amenity-page-${pageIndex}`}
                                              aria-hidden={!isActiveAmenitySlide}
                                              className="grid w-full shrink-0 grid-cols-1 gap-2.5 min-[520px]:grid-cols-2"
                                            >
                                              {pageGroups.map((group) => {
                                                const GroupIcon = group.icon;
                                                const totalCount = group.items.length + group.missingItems.length;
                                                return (
                                                  <article
                                                    key={group.key}
                                                    data-testid="amenity-group-card"
                                                    className="rounded-[16px] border border-white/10 bg-[#171d2e] px-4 py-3.5 sm:px-5 sm:py-4"
                                                  >
                                                    <div className="flex items-center justify-between gap-3">
                                                      <div className="flex min-w-0 items-center gap-2.5">
                                                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-[8px] border border-white/10 bg-[#1b2336]">
                                                          <GroupIcon className={`h-3.5 w-3.5 ${group.iconAccent}`} />
                                                        </span>
                                                        <h3 className="text-sm font-semibold leading-tight text-[#F3EEDF] sm:text-[15px]">
                                                          {group.title}
                                                        </h3>
                                                      </div>
                                                      <span className="shrink-0 text-[11px] font-medium text-white/58 sm:text-xs">
                                                        {group.items.length} / {totalCount}
                                                      </span>
                                                    </div>

                                                    <div className="mt-2.5 h-px bg-white/8" />

                                                    <ul className="mt-3 flex flex-wrap gap-2">
                                                      {group.items.map((item) => (
                                                        <li
                                                          key={`${group.key}-${item}`}
                                                          className="inline-flex items-center rounded-[10px] border border-[#2f5d48] bg-[#183126] px-3 py-1.5 text-[11px] font-medium leading-none text-[#d8f7e4] sm:text-xs"
                                                        >
                                                          <span className="mr-2 text-[#57d487]">✓</span>
                                                          {item}
                                                        </li>
                                                      ))}
                                                    </ul>

                                                    {group.missingItems.length > 0 && (
                                                      <p className="mt-3 text-[11px] leading-snug text-white/45 sm:text-xs">
                                                        Not available: {group.missingItems.join(', ')}
                                                      </p>
                                                    )}
                                                  </article>
                                                );
                                              })}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>

                                    {amenityPages.length > 1 && (
                                      <div className="mt-4 flex items-center justify-between px-0.5">
                                        <div className="flex items-center justify-center gap-2">
                                          {amenityPages.map((pageGroups, carouselIndex) => (
                                            <button
                                              key={`amenity-page-${carouselIndex}-pagination`}
                                              type="button"
                                              aria-label={`Go to amenity page ${carouselIndex + 1}`}
                                              onClick={() => setActiveAmenitySlide(carouselIndex)}
                                              className="group flex items-center py-1"
                                            >
                                              <span
                                                className={`block h-2.5 rounded-full transition-all duration-300 ${
                                                  carouselIndex === activeAmenitySlide
                                                    ? 'w-5 bg-[#F3BB36]'
                                                    : 'w-2.5 bg-white/25 group-hover:bg-white/45'
                                                }`}
                                              />
                                              <span className="sr-only">
                                                View amenity page {carouselIndex + 1} of {amenityPages.length}
                                              </span>
                                            </button>
                                          ))}
                                        </div>

                                        <div className="inline-flex items-center gap-2">
                                          <button
                                            type="button"
                                            aria-label="Previous amenity group"
                                            onClick={() => setActiveAmenitySlide((current) => (
                                              current === 0 ? amenityPages.length - 1 : current - 1
                                            ))}
                                            className="flex h-[30px] w-[30px] items-center justify-center rounded-full border border-white/10 bg-white/[0.06] text-[#F3EEDF] transition-colors hover:bg-white/10"
                                          >
                                            <ArrowLeftIcon className="h-3.5 w-3.5" />
                                          </button>
                                          <button
                                            type="button"
                                            aria-label="Next amenity group"
                                            onClick={() => setActiveAmenitySlide((current) => (
                                              current === amenityPages.length - 1 ? 0 : current + 1
                                            ))}
                                            className="flex h-[30px] w-[30px] items-center justify-center rounded-full border border-white/10 bg-white/[0.06] text-[#F3EEDF] transition-colors hover:bg-white/10"
                                          >
                                            <ArrowRightIcon className="h-3.5 w-3.5" />
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </section>
                      );
                    })()}
                  </div>

                  {selectedLocation?.id && (
                    <div className="mt-3">
                      <InlineReviews
                        locationId={selectedLocation.id}
                        onSeeAll={() => handleRatingClick(selectedLocation.id, 'pricing_inline_reviews')}
                      />
                    </div>
                  )}

                  {featureFlags.publicHiringEnabled && (
                    <section
                      className="mt-3 rounded-lg border border-[var(--accent-500)]/25 bg-[#08172f]/84 px-3 py-2 sm:px-4 sm:py-2.5"
                      aria-label="Driver hiring callout"
                    >
                      <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          <p className="text-xs leading-5 text-[#d8e6ff]/88 sm:text-sm sm:leading-5">
                            OTR positions open. Optional 60-second intake with recruiter follow-up.
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
                          <Link
                            to="/hiring/otr"
                            onClick={() => logEvent('hiring_cta_click', {
                              source: 'home_pricing_context',
                              placement: 'pricing_section_primary',
                              track: 'otr',
                            })}
                            className="inline-flex h-8 items-center justify-center rounded-md border border-[var(--accent-500)]/55 bg-[var(--accent-500)]/12 px-3 text-[11px] font-semibold text-[var(--accent-500)] transition-colors hover:bg-[var(--accent-500)]/20 sm:h-9 sm:text-xs"
                          >
                            OTR Intake
                          </Link>
                          <Link
                            to="/hiring"
                            onClick={() => logEvent('hiring_cta_click', {
                              source: 'home_pricing_context',
                              placement: 'pricing_section_secondary',
                              track: 'all_routes',
                            })}
                            className="inline-flex h-8 items-center justify-center rounded-md border border-[#3a5678] px-3 text-[11px] font-medium text-[#9cc3f4] transition-colors hover:border-[#4d6d94] hover:text-[#c3dcff] sm:h-9 sm:text-xs"
                          >
                            All Routes
                          </Link>
                        </div>
                      </div>
                    </section>
                  )}

                  
                </div>
              </>
            )}
            <div
              ref={pricingLocationSectionRef}
              className={`
                w-full self-stretch relative transition-[filter] duration-300
                ${(!selectedLocation && (isMapFocusMode || shouldForceDesktopHomeMapListVisible)) ? 'drop-shadow-[0_14px_24px_rgba(184,134,11,0.18)]' : ''}
              `}
              style={{ overflowAnchor: 'none', width: '100%' }}
            >
              {historicalPricingLocation && (
                <div className="mx-auto mt-3 w-full max-w-7xl px-2 sm:px-3 md:px-4">
                  <div className="rounded-lg border border-[#daa520]/30 bg-[#0d1726] px-4 py-3 sm:px-5">
                    <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#daa520]">Historical location</p>
                    <p className="mt-1 text-sm font-semibold text-[#f8f3e8]">{historicalPricingLocation.title}</p>
                    <p className="mt-0.5 text-sm text-[#c9d3e3]/75">{historicalPricingLocation.address}</p>
                  </div>
                </div>
              )}
              {isMapActivated ? (
                <MapComponent
                  {...generateMapComponentProps({
                    addresses: historicalPricingLocation ? [historicalPricingLocation] : addresses,
                    filteredAddresses: historicalPricingLocation ? [historicalPricingLocation] : locationsForResults,
                    mapState: {
                      mapCenter: historicalPricingLocation?.coordinates || mapCenter,
                      mapZoom: historicalPricingLocation ? 15 : mapZoom
                    },
                    showAddresses,
                    setShowAddresses,
                    addressCardsRef,
                    lotsAvailableByLocationId,
                    nextAvailableDates,
                    handleAddressClick,
                    handleShowAddressesClick,
                    handleBookNowClick,
                    // In pricing routes, tie map layout recalculation to selectedLocation hydration
                    // so entry flows from Locations tab and State Card converge on identical map size.
                    selectedAddress: isPricingRoute
                      ? (selectedLocation?.address || historicalPricingLocation?.address || null)
                      : selectedAddress,
                    // Keep pricing map mode stable from initial route render.
                    // Using the URL id avoids a temporary browse-mode mount before
                    // selectedLocation is hydrated from context.
                    selectedLocationId: isPricingRoute
                      ? (historicalPricingLocation?.id || normalizedPricingLocationId || selectedLocation?.id)
                      : null,
                    activeStateFilter: stateFilter,
                    forceAddressListVisible: shouldForceDesktopHomeMapListVisible,
                    hideAddressToggle: shouldForceDesktopHomeMapListVisible,
                  })}
                />
              ) : (
                <div
                  ref={mapPlaceholderRef}
                  data-testid="map-placeholder"
                  className="w-full max-w-7xl mx-auto px-2 sm:px-3 md:px-4 my-3"
                >
                  <div className="relative overflow-hidden rounded-lg bg-[#0d1726]">
                    <div className="relative h-[300px] md:h-[400px] lg:h-[450px]">
                      {mapPreviewUrl ? (
                        <img
                          src={mapPreviewUrl}
                          alt="Parking locations map preview"
                          width="640"
                          height="225"
                          className="block w-full h-full object-cover"
                          loading="eager"
                          decoding="async"
                          fetchpriority={isPricingRoute ? 'high' : 'low'}
                        />
                      ) : (
                        <div className="h-full w-full bg-[#14324a]/35 animate-pulse">
                          <div className="h-full w-full bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer" />
                        </div>
                      )}
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#02060d]/70 via-transparent to-[#02060d]/25" />
                      <div className="absolute top-3 right-3 sm:top-4 sm:right-4">
                        <button
                          type="button"
                          data-testid="activate-map-button"
                          onClick={handleMapPlaceholderCtaClick}
                          className="px-3 py-1.5 rounded-md border border-[var(--accent-500)]/90 bg-[#071020]/70 text-[var(--accent-500)] text-xs sm:text-sm backdrop-blur-sm hover:bg-[var(--accent-500)]/12 transition-colors"
                        >
                          View locations on a map
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {isHomepageRoute && !selectedLocation && homepageDiscoveryContent && (
              <TruckParkingDiscoverySections
                content={homepageDiscoveryContent}
                showHeading={false}
              />
            )}
            {featureFlags.publicHiringEnabled && isHomepageRoute && !selectedLocation && <HomepageHiringRail />}
            {isTruckParkingRoute && !selectedLocation && truckParkingRouteContent && (
              <TruckParkingDiscoverySections content={truckParkingRouteContent} />
            )}
            {isPricingRoute && selectedLocation && (
              <LocationSeoSections
                location={selectedLocation}
                allLocations={addresses}
                canonicalPath={selectedLocationCanonicalPath}
                onBookLocationNowClick={handleSeoBookLocationNowClick}
              />
            )}
            {showBookingModal && bookingDetails?.parkingLotId && (
              <BookingModal
                initialLotData={{
                    address: bookingDetails.address,
                    parkingLotId: bookingDetails.parkingLotId,
                    duration: bookingDetails.duration,
                    pricingContext: bookingDetails.pricingContext,
                    gateCode: bookingDetails.gateCode,
                    googlePlaceId: bookingDetails.googlePlaceId,
                    isPromoActive: bookingDetails.isPromoActive,
                    location: bookingDetails.location || selectedLocation // Pass location object for pricing
                }}
                onClose={() => { setShowBookingModal(false); setBookingSource(null); }}
                onSubmit={handleBookingSubmit}
              />
            )}
            {showReviewModal && (
              <ReadReviewModal 
                isOpen={showReviewModal}
                onClose={handleCloseReviewModal} 
                locationAddress={selectedAddress || (ratingLocationId ? getLocationById(ratingLocationId)?.address : '')} 
                locationId={ratingLocationId || ''} 
              />
            )}
          </>
        )}
     {message && (
        <MessageModal
          message={message}
          onClose={() => {
            setMessage(null);
            handleBackToStatesClick();
          }}
        />
      )}
      </Suspense>
    </div>
  );
};

export default Home;
